import {
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Alert,
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  TextInput,
  View,
} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useRoute} from '@react-navigation/native'
import {setStringAsync} from 'expo-clipboard'

import {CHAT_DEFAULT_HOMESERVER} from '#/lib/constants'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {
  createGroupRoom,
  ensureDirectRoom,
  getClient,
  getMatrixBaseUrl,
  inviteToRoom,
  leaveRoom,
  listIncomingInvites,
  listJoinedRooms,
  listOutgoingPending,
  loginMatrix,
  loginWithToken,
  logoutMatrix,
  normalizeMatrixBaseUrl,
  redactEvent,
  sendMedia,
  sendReaction,
  sendTextMessage,
  setMatrixBaseUrl,
  uploadMediaContent,
  updateMyMatrixProfile,
} from '#/lib/matrixClient'
import {openCamera, openPicker, openUnifiedPicker} from '#/lib/media/picker'
import {isWeb} from '#/platform/detection'
import {
  clearMatrixSession,
  loadMatrixConfig,
  loadMatrixSession,
  type MatrixSession,
  saveMatrixConfig,
  saveMatrixSession,
} from '#/lib/storage/matrix'
import {cleanError, isNetworkError} from '#/lib/strings/errors'
import {
  displayMatrix,
  normalizeMatrixIdentifier,
} from '#/lib/strings/identifiers'
import {validateMatrixId} from '#/lib/tellusProfileRecord'
import {logger} from '#/logger'
import {useActivationActions} from '#/state/activation'
import {useSetMinimalShellMode} from '#/state/shell'
import {TimeElapsed} from '#/view/com/util/TimeElapsed'
import * as Toast from '#/view/com/util/Toast'
import {Card} from '#/screens/Messages/components/Card'
import {PageShell} from '#/screens/Messages/components/PageShell'
import {StateCard} from '#/screens/Messages/components/StateCard'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as TextField from '#/components/forms/TextField'
import {ArrowRotateCounterClockwise_Stroke2_Corner0_Rounded as RetryIcon} from '#/components/icons/ArrowRotateCounterClockwise'
import {CircleInfo_Stroke2_Corner0_Rounded as CircleInfoIcon} from '#/components/icons/CircleInfo'
import {ChevronLeft_Stroke2_Corner0_Rounded as ChevronLeft} from '#/components/icons/Chevron'
import {Camera_Stroke2_Corner0_Rounded as CameraIcon} from '#/components/icons/Camera'
import {EmojiSmile_Stroke2_Corner0_Rounded as EmojiIcon} from '#/components/icons/Emoji'
import {Image_Stroke2_Corner0_Rounded as ImageIcon} from '#/components/icons/Image'
import {MagnifyingGlass_Filled_Stroke2_Corner0_Rounded as SearchIcon} from '#/components/icons/MagnifyingGlass'
import {Message_Stroke2_Corner0_Rounded as MessageIcon} from '#/components/icons/Message'
import {PaperPlane_Stroke2_Corner0_Rounded as SendIcon} from '#/components/icons/PaperPlane'
import {Phone_Stroke2_Corner0_Rounded as PhoneIcon} from '#/components/icons/Phone'
import {PersonGroup_Stroke2_Corner2_Rounded as GroupIcon} from '#/components/icons/Person'
import {PlusLarge_Stroke2_Corner0_Rounded as PlusIcon} from '#/components/icons/Plus'
import {SettingsGear2_Stroke2_Corner0_Rounded as SettingsIcon} from '#/components/icons/SettingsGear2'
import {SpeakerVolumeFull_Stroke2_Corner0_Rounded as VoiceIcon} from '#/components/icons/Speaker'
import {VideoClip_Stroke2_Corner0_Rounded as VideoIcon} from '#/components/icons/VideoClip'
import {Text} from '#/components/Typography'
import * as Layout from '#/components/Layout'
import {chatTokens, glassSurface} from '#/screens/MessagesMatrix/chatTokens'

type Status = 'loading' | 'needs-login' | 'connecting' | 'ready'

type RoomMessage = {
  id: string
  sender: string
  body: string
  ts: number
  isMine: boolean
  msgtype: string
  mediaUrl?: string | null
  mediaHttpUrl?: string | null
  mediaMimeType?: string | null
  fileName?: string | null
  edited?: boolean
  deleted?: boolean
  status?: 'sending' | 'failed' | 'sent'
  replyTo?: string | null
  reactions?: Record<
    string,
    {count: number; myReactionIds: string[]; me: boolean}
  >
}

type MessageContextState = {
  message: RoomMessage
  x: number
  y: number
}

type PendingUpload = {
  roomId: string
  messageId: string
  file: Blob
  name: string
  mimeType: string
  msgtype: string
  info?: Record<string, any>
  replyTo?: string | null
}

type ReactionIndexEntry = {
  targetId: string
  emoji: string
  sender: string
  eventId: string
}

const DEFAULT_HOMESERVER = CHAT_DEFAULT_HOMESERVER
const LONG_PRESS_DELAY_MS = 520
const LONG_PRESS_MOVE_THRESHOLD = 10

function deriveHomeserverFromMatrixId(value: string) {
  const match = value.trim().match(/^@[^:\s]+:([^/\s]+)$/)
  if (!match) return ''
  return `https://${match[1]}`
}

function formatChatError(err: unknown) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  if (offline) return 'Aucune connexion Internet.'
  if (isNetworkError(err)) {
    return 'Service temporairement indisponible. Réessayez.'
  }
  return cleanError(err) || 'Échec de la connexion Tellus Chat.'
}

function getEventType(event: any) {
  return event?.getType?.() ?? event?.event?.type ?? event?.type
}

function getEventContent(event: any) {
  return event?.getContent?.() ?? event?.event?.content ?? event?.content
}

function getEventId(event: any) {
  return event?.getId?.() ?? event?.event?.event_id
}

function getEventSender(event: any) {
  return event?.getSender?.() ?? event?.event?.sender ?? ''
}

function getEventTs(event: any) {
  return event?.getTs?.() ?? event?.event?.origin_server_ts ?? Date.now()
}

function getMessageBodyFromContent(content: any) {
  if (!content) return ''
  const msgtype = content.msgtype
  if (msgtype === 'm.text' && content.body) return String(content.body)
  if (msgtype === 'm.image') return String(content.body || 'Photo')
  if (msgtype === 'm.video') return String(content.body || 'Vidéo')
  if (msgtype === 'm.audio') return String(content.body || 'Message audio')
  if (msgtype === 'm.file') return String(content.body || 'Fichier')
  if (content.body) return String(content.body)
  return ''
}

function guessMsgtypeFromMime(mimeType?: string | null) {
  if (!mimeType) return 'm.file'
  if (mimeType.startsWith('image/')) return 'm.image'
  if (mimeType.startsWith('video/')) return 'm.video'
  if (mimeType.startsWith('audio/')) return 'm.audio'
  return 'm.file'
}

function defaultBodyForMsgtype(msgtype: string) {
  if (msgtype === 'm.image') return 'Photo'
  if (msgtype === 'm.video') return 'Vidéo'
  if (msgtype === 'm.audio') return 'Message audio'
  if (msgtype === 'm.file') return 'Fichier'
  return 'Message'
}

function formatMatrixMessage(
  event: any,
  currentUserId: string,
  matrixClient?: any,
): RoomMessage | null {
  const type = getEventType(event)
  if (type !== 'm.room.message') return null
  const content = getEventContent(event)
  if (!content || !content.msgtype) return null

  const relatesTo = content?.['m.relates_to']
  if (relatesTo?.rel_type === 'm.replace') {
    return null
  }

  const sender = getEventSender(event)
  const id = getEventId(event) ?? `${sender || 'user'}-${getEventTs(event)}`
  const ts = getEventTs(event)
  const isRedacted =
    event?.isRedacted?.() || Boolean(event?.event?.unsigned?.redacted_because)
  const body = isRedacted ? 'Message supprimé' : getMessageBodyFromContent(content)
  const replyTo = relatesTo?.['m.in_reply_to']?.event_id ?? null
  const mediaUrl = content.url || null
  const mediaHttpUrl =
    mediaUrl && typeof matrixClient?.mxcUrlToHttp === 'function'
      ? matrixClient.mxcUrlToHttp(mediaUrl)
      : null
  const info = content?.info || {}
  return {
    id: String(id),
    sender: String(sender),
    body,
    ts: Number(ts) || Date.now(),
    isMine: sender === currentUserId,
    msgtype: String(content.msgtype || 'm.text'),
    mediaUrl,
    mediaHttpUrl,
    mediaMimeType: info?.mimetype || content?.mimetype || null,
    fileName: content.body || null,
    replyTo,
    deleted: isRedacted || undefined,
  }
}

function applyEditToMessage(message: RoomMessage, newBody: string) {
  return {
    ...message,
    body: newBody,
    edited: true,
  }
}

function markMessageDeleted(message: RoomMessage) {
  return {
    ...message,
    body: 'Message supprimé',
    deleted: true,
    mediaUrl: null,
    mediaHttpUrl: null,
    mediaMimeType: null,
    fileName: null,
    reactions: message.reactions,
  }
}

function addReactionToMessage(
  message: RoomMessage,
  emoji: string,
  sender: string,
  reactionEventId: string,
  currentUserId: string,
) {
  const reactions = {...(message.reactions || {})}
  const existing = reactions[emoji] || {
    count: 0,
    myReactionIds: [],
    me: false,
  }
  const myReactionIds = [...existing.myReactionIds]
  if (sender === currentUserId && !myReactionIds.includes(reactionEventId)) {
    myReactionIds.push(reactionEventId)
  }
  reactions[emoji] = {
    count: existing.count + 1,
    myReactionIds,
    me: myReactionIds.length > 0,
  }
  return {
    ...message,
    reactions,
  }
}

function removeReactionFromMessage(
  message: RoomMessage,
  emoji: string,
  sender: string,
  reactionEventId: string,
  currentUserId: string,
) {
  if (!message.reactions || !message.reactions[emoji]) return message
  const reactions = {...message.reactions}
  const existing = reactions[emoji]
  const nextCount = Math.max(0, existing.count - 1)
  let myReactionIds = existing.myReactionIds
  if (sender === currentUserId) {
    myReactionIds = existing.myReactionIds.filter(id => id !== reactionEventId)
  }
  if (nextCount <= 0) {
    delete reactions[emoji]
  } else {
    reactions[emoji] = {
      count: nextCount,
      myReactionIds,
      me: myReactionIds.length > 0,
    }
  }
  return {
    ...message,
    reactions,
  }
}

function buildRoomMessages(
  events: any[],
  currentUserId: string,
  matrixClient?: any,
) {
  const messages: RoomMessage[] = []
  const messageMap = new Map<string, RoomMessage>()
  const reactionIndex = new Map<string, ReactionIndexEntry>()
  const pendingEdits = new Map<string, string>()
  const pendingReactions = new Map<string, ReactionIndexEntry[]>()
  const pendingRedactions = new Set<string>()

  events.forEach(event => {
    const type = getEventType(event)
    if (type === 'm.room.message') {
      const content = getEventContent(event)
      const relatesTo = content?.['m.relates_to']
      if (relatesTo?.rel_type === 'm.replace') {
        const targetId = relatesTo?.event_id
        const nextBody =
          content?.['m.new_content']?.body || content?.body || ''
        if (targetId) {
          const target = messageMap.get(targetId)
          if (target) {
            messageMap.set(targetId, applyEditToMessage(target, nextBody))
          } else {
            pendingEdits.set(targetId, nextBody)
          }
        }
        return
      }

      const parsed = formatMatrixMessage(event, currentUserId, matrixClient)
      if (!parsed) return

      if (pendingRedactions.has(parsed.id)) {
        parsed.deleted = true
        parsed.body = 'Message supprimé'
        pendingRedactions.delete(parsed.id)
      }

      if (pendingEdits.has(parsed.id)) {
        parsed.body = pendingEdits.get(parsed.id) || parsed.body
        parsed.edited = true
        pendingEdits.delete(parsed.id)
      }

      const pending = pendingReactions.get(parsed.id)
      if (pending && pending.length) {
        let next = parsed
        pending.forEach(entry => {
          next = addReactionToMessage(
            next,
            entry.emoji,
            entry.sender,
            entry.eventId,
            currentUserId,
          )
        })
        parsed.reactions = next.reactions
        pendingReactions.delete(parsed.id)
      }

      messageMap.set(parsed.id, parsed)
      messages.push(parsed)
      return
    }

    if (type === 'm.reaction') {
      const content = getEventContent(event)
      const relatesTo = content?.['m.relates_to']
      if (relatesTo?.rel_type !== 'm.annotation') return
      const targetId = relatesTo?.event_id
      const emoji = relatesTo?.key
      const reactionEventId = getEventId(event)
      const sender = getEventSender(event)
      if (!targetId || !emoji || !reactionEventId) return
      reactionIndex.set(String(reactionEventId), {
        targetId,
        emoji,
        sender,
        eventId: String(reactionEventId),
      })
      const target = messageMap.get(targetId)
      if (target) {
        messageMap.set(
          targetId,
          addReactionToMessage(
            target,
            emoji,
            sender,
            String(reactionEventId),
            currentUserId,
          ),
        )
      } else {
        const list = pendingReactions.get(targetId) || []
        list.push({
          targetId,
          emoji,
          sender,
          eventId: String(reactionEventId),
        })
        pendingReactions.set(targetId, list)
      }
      return
    }

    if (type === 'm.room.redaction') {
      const redacts =
        event?.event?.redacts ??
        event?.getRedacts?.() ??
        getEventContent(event)?.redacts
      if (!redacts) return
      const reaction = reactionIndex.get(String(redacts))
      if (reaction) {
        const target = messageMap.get(reaction.targetId)
        if (target) {
          messageMap.set(
            reaction.targetId,
            removeReactionFromMessage(
              target,
              reaction.emoji,
              reaction.sender,
              String(redacts),
              currentUserId,
            ),
          )
        }
        reactionIndex.delete(String(redacts))
        return
      }
      const target = messageMap.get(String(redacts))
      if (target) {
        messageMap.set(String(redacts), markMessageDeleted(target))
      } else {
        pendingRedactions.add(String(redacts))
      }
    }
  })

  const sorted = [...messageMap.values()].sort((a, b) => a.ts - b.ts)
  return {messages: sorted, messageMap, reactionIndex}
}

function formatChatDate(ts: number) {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(new Date(ts))
  } catch {
    return new Date(ts).toLocaleDateString()
  }
}

function formatChatTime(ts: number) {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(ts))
  } catch {
    return new Date(ts).toLocaleTimeString()
  }
}

function getInitials(label: string) {
  const clean = label.trim()
  if (!clean) return ''
  const parts = clean.split(/\s+/)
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}

function getOtherMember(room: any, currentUserId: string) {
  const members = room?.getJoinedMembers?.() || room?.getMembers?.() || []
  return members.find((member: any) => member?.userId !== currentUserId)
}

function getRoomMembers(room: any) {
  return room?.getJoinedMembers?.() || room?.getMembers?.() || []
}

function isDirectRoom(room: any) {
  if (room?.isDirectMessageRoom?.() === true) return true
  const members = getRoomMembers(room)
  const name = room?.name || room?.getName?.()
  return members.length === 2 && !name
}

function getMemberDisplayName(member: any) {
  return (
    member?.name ||
    member?.rawDisplayName ||
    displayMatrix(member?.userId || '') ||
    ''
  )
}

function getMemberAvatarUrl(client: any, member: any) {
  if (!client || !member) return null
  const baseUrl =
    (typeof client.getHomeserverUrl === 'function' &&
      client.getHomeserverUrl()) ||
    client.baseUrl ||
    getMatrixBaseUrl() ||
    ''
  if (!baseUrl) return null
  if (typeof member.getAvatarUrl === 'function') {
    return member.getAvatarUrl(baseUrl, 96, 96, 'crop', false, true)
  }
  const mxc = member?.avatarUrl || member?.mxcAvatarUrl
  if (mxc && typeof client.mxcUrlToHttp === 'function') {
    return client.mxcUrlToHttp(mxc, 96, 96, 'crop')
  }
  return null
}

function getRoomLastMessage(
  room: any,
  currentUserId: string,
  matrixClient?: any,
) {
  const timeline = room?.getLiveTimeline?.()
  const events = timeline?.getEvents?.() ?? room?.timeline ?? []
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const parsed = formatMatrixMessage(events[i], currentUserId, matrixClient)
    if (parsed) return parsed
  }
  return null
}

function getUnreadCount(room: any) {
  const count =
    room?.getUnreadNotificationCount?.('total') ??
    room?.getUnreadNotificationCount?.() ??
    room?.unreadNotificationCount
  return typeof count === 'number' ? count : null
}

export function MessagesScreen() {
  const {_} = useLingui()
  const t = useTheme()
  const {isMobile} = useWebMediaQueries()
  const route = useRoute<any>()
  const {setChatStatus} = useActivationActions()
  const setMinimalShellMode = useSetMinimalShellMode()
  const [status, setStatus] = useState<Status>('loading')
  const [session, setSession] = useState<MatrixSession | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)

  const [homeserverInput, setHomeserverInput] = useState(DEFAULT_HOMESERVER)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [stayLoggedIn, setStayLoggedIn] = useState(true)
  const [emailHelp, setEmailHelp] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [hasCustomHomeserver, setHasCustomHomeserver] = useState(false)

  const [rooms, setRooms] = useState<any[]>([])
  const [incomingCount, setIncomingCount] = useState(0)
  const [outgoingCount, setOutgoingCount] = useState(0)

  const [newConversationOpen, setNewConversationOpen] = useState(false)
  const [newConversationUser, setNewConversationUser] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [newGroupOpen, setNewGroupOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupDescription, setNewGroupDescription] = useState('')
  const [isCreatingGroup, setIsCreatingGroup] = useState(false)
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null)
  const [groupPanelOpen, setGroupPanelOpen] = useState(false)
  const [inviteInput, setInviteInput] = useState('')
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [isInviting, setIsInviting] = useState(false)
  const [leaveError, setLeaveError] = useState<string | null>(null)
  const [roomMessages, setRoomMessages] = useState<RoomMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesError, setMessagesError] = useState<string | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const [messageInput, setMessageInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [contextMenu, setContextMenu] = useState<MessageContextState | null>(
    null,
  )
  const [replyPreview, setReplyPreview] = useState<RoomMessage | null>(null)
  const [editTarget, setEditTarget] = useState<RoomMessage | null>(null)
  const [cameraMenuOpen, setCameraMenuOpen] = useState(false)
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false)
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)
  const [emojiPickerMode, setEmojiPickerMode] = useState<
    'compose' | 'reaction' | null
  >(null)
  const [reactionTarget, setReactionTarget] = useState<RoomMessage | null>(null)
  const [forwardTarget, setForwardTarget] = useState<RoomMessage | null>(null)
  const [forwardOpen, setForwardOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [profileDisplayName, setProfileDisplayName] = useState('')
  const [profileAvatarUrl, setProfileAvatarUrl] = useState<string | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileSaving, setProfileSaving] = useState(false)
  const latestRoomId = useRef<string | null>(null)
  const messageListRef = useRef<ScrollView | null>(null)
  const reactionIndexRef = useRef<Map<string, ReactionIndexEntry>>(new Map())
  const pendingUploadsRef = useRef<Map<string, PendingUpload>>(new Map())

  const resolvedHomeserver = useMemo(() => {
    return normalizeMatrixBaseUrl(homeserverInput) || DEFAULT_HOMESERVER
  }, [homeserverInput])
  const hasSession = Boolean(session?.accessToken && session?.userId)
  const isReady = status === 'ready'
  const isConnecting = status === 'connecting'
  const matrixClient = getClient()
  const currentUserId = session?.userId || matrixClient?.getUserId?.() || ''

  const refreshRooms = useCallback(() => {
    try {
      setRooms([...listJoinedRooms()])
      setIncomingCount(listIncomingInvites().length)
      setOutgoingCount(listOutgoingPending().length)
    } catch (err) {
      logger.error('Tellus Chat refresh failed', {message: err})
    }
  }, [])

  const connectWithSession = useCallback(async () => {
    if (!session?.accessToken || !session?.userId) return
    setError(null)
    try {
      setMatrixBaseUrl(resolvedHomeserver)
      await loginWithToken({
        access_token: session.accessToken,
        user_id: session.userId,
        device_id: session.deviceId,
      })
      await saveMatrixConfig({homeserver: resolvedHomeserver})
      setStatus('ready')
      setChatStatus('connected')
      refreshRooms()
    } catch (err) {
      const message = formatChatError(err)
      setError(message)
      setChatStatus('error', message)
      setStatus('needs-login')
    }
  }, [resolvedHomeserver, session, refreshRooms, setChatStatus])

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const [savedConfig, savedSession] = await Promise.all([
          loadMatrixConfig(),
          loadMatrixSession(),
        ])
        if (!active) return
        const normalizedHomeserver = savedConfig?.homeserver
          ? normalizeMatrixBaseUrl(savedConfig.homeserver)
          : ''
        const derivedFromSession =
          !normalizedHomeserver && savedSession?.userId
            ? deriveHomeserverFromMatrixId(savedSession.userId)
            : ''
        const initialHomeserver =
          normalizedHomeserver || derivedFromSession || DEFAULT_HOMESERVER
        setSession(savedSession ?? null)
        setHomeserverInput(initialHomeserver)
        setMatrixBaseUrl(initialHomeserver)
        setHasCustomHomeserver(
          Boolean(
            normalizedHomeserver && normalizedHomeserver !== DEFAULT_HOMESERVER,
          ),
        )
        if (savedSession?.accessToken && savedSession?.userId) {
          setStatus('connecting')
          setChatStatus('connecting')
        } else {
          setStatus('needs-login')
          setChatStatus('disconnected')
        }
      } catch (err) {
        logger.error('Tellus Chat storage load failed', {message: err})
        if (!active) return
        setStatus('needs-login')
        setChatStatus('disconnected')
      }
    }

    load()
    return () => {
      active = false
    }
  }, [setChatStatus])

  useEffect(() => {
    if (status !== 'connecting') return
    connectWithSession()
  }, [status, connectWithSession])

  useEffect(() => {
    if (status !== 'ready') return
    const client = getClient()
    const onSync = (state: string) => {
      if (state === 'PREPARED') {
        refreshRooms()
      }
    }
    client?.on?.('sync', onSync)
    return () => client?.removeListener?.('sync', onSync)
  }, [status, refreshRooms])

  const onLogin = useCallback(async () => {
    const trimmedUser = username.trim()
    if (!trimmedUser || !password) return
    const normalizedHomeserver = normalizeMatrixBaseUrl(homeserverInput)
    if (!normalizedHomeserver) {
      setConfigError(_(msg`Serveur invalide.`))
      setAdvancedOpen(true)
      return
    }
    setConfigError(null)
    setStatus('connecting')
    setChatStatus('connecting')
    setError(null)
    try {
      setMatrixBaseUrl(normalizedHomeserver)
      const normalizedUser = normalizeMatrixIdentifier(
        trimmedUser,
        normalizedHomeserver,
      )
      const response = await loginMatrix(normalizedUser, password)
      const nextSession = {
        accessToken: response.access_token,
        userId: response.user_id,
        deviceId: response.device_id,
      }
      setSession(nextSession)
      await saveMatrixConfig({homeserver: normalizedHomeserver})
      if (stayLoggedIn) {
        await saveMatrixSession(nextSession)
      } else {
        await clearMatrixSession()
      }
      setEmailHelp(false)
      setPassword('')
      setStatus('ready')
      setChatStatus('connected')
      refreshRooms()
    } catch (err) {
      const message = formatChatError(err)
      const looksLikeEmail =
        trimmedUser.includes('@') && !trimmedUser.startsWith('@')
      if (looksLikeEmail) {
        setEmailHelp(true)
      }
      setError(message)
      setChatStatus('error', message)
      setStatus('needs-login')
    }
  }, [
    username,
    password,
    homeserverInput,
    stayLoggedIn,
    refreshRooms,
    setChatStatus,
    _,
  ])

  const onLogout = useCallback(async () => {
    await logoutMatrix()
    await clearMatrixSession()
    setSession(null)
    setError(null)
    setRooms([])
    setIncomingCount(0)
    setOutgoingCount(0)
    setNewConversationOpen(false)
    setNewConversationUser('')
    setNewGroupOpen(false)
    setNewGroupName('')
    setNewGroupDescription('')
    setSelectedRoomId(null)
    setGroupPanelOpen(false)
    setInviteInput('')
    setInviteError(null)
    setIsInviting(false)
    setLeaveError(null)
    setRoomMessages([])
    setMessagesError(null)
    setSendError(null)
    setMessageInput('')
    setContextMenu(null)
    setReplyPreview(null)
    setEditTarget(null)
    setCameraMenuOpen(false)
    setAttachmentMenuOpen(false)
    setEmojiPickerOpen(false)
    setEmojiPickerMode(null)
    setReactionTarget(null)
    setForwardTarget(null)
    setForwardOpen(false)
    setSettingsOpen(false)
    setProfileDisplayName('')
    setProfileAvatarUrl(null)
    setProfileLoading(false)
    setProfileSaving(false)
    setStatus('needs-login')
    setChatStatus('disconnected')
    reactionIndexRef.current = new Map()
    pendingUploadsRef.current.clear()
  }, [setChatStatus])

  const onRetry = useCallback(() => {
    setError(null)
    if (session?.accessToken && session?.userId) {
      setStatus('connecting')
    } else {
      setStatus('needs-login')
    }
  }, [session])

  const onCreateConversation = useCallback(async () => {
    if (status !== 'ready') return
    const value = newConversationUser.trim()
    if (!value || isCreating) return
    setIsCreating(true)
    try {
      const normalizedUser = normalizeMatrixIdentifier(value, homeserverInput)
      await ensureDirectRoom(normalizedUser)
      setNewConversationUser('')
      setNewConversationOpen(false)
      setNewGroupOpen(false)
      refreshRooms()
    } catch (err) {
      const message =
        cleanError(err) || _(msg`Impossible de créer la conversation.`)
      setError(message)
    } finally {
      setIsCreating(false)
    }
  }, [
    status,
    newConversationUser,
    isCreating,
    homeserverInput,
    refreshRooms,
    _,
  ])

  const conversationLabel = useMemo(() => _(msg`Conversation`), [_])
  const groupLabel = useMemo(() => _(msg`Groupe`), [_])

  const roomSummaries = useMemo(() => {
    const summaries = rooms.map(room => {
      const roomId = room?.roomId ?? null
      const isDirect = isDirectRoom(room)
      const member = isDirect ? getOtherMember(room, currentUserId) : null
      const displayName = isDirect
        ? getMemberDisplayName(member) ||
          room?.name ||
          room?.getName?.() ||
          conversationLabel
        : room?.name || room?.getName?.() || groupLabel
      const avatarUrl = isDirect
        ? getMemberAvatarUrl(matrixClient, member)
        : null
      const lastMessage = getRoomLastMessage(room, currentUserId, matrixClient)
      const lastMessagePreview = lastMessage
        ? lastMessage.isMine
          ? _(msg`Vous: ${lastMessage.body}`)
          : lastMessage.body
        : _(msg`Aucun message`)
      const rawLastMessageTs =
        lastMessage?.ts ?? room?.getLastActiveTimestamp?.() ?? null
      const lastMessageTs =
        typeof rawLastMessageTs === 'number' && rawLastMessageTs > 0
          ? rawLastMessageTs
          : null
      const unreadCount = getUnreadCount(room)
      return {
        room,
        roomId,
        displayName,
        avatarUrl,
        initials: getInitials(displayName),
        isGroup: !isDirect,
        lastMessagePreview,
        lastMessageTs,
        unreadCount,
      }
    })
    return summaries.sort(
      (a, b) => (b.lastMessageTs ?? 0) - (a.lastMessageTs ?? 0),
    )
  }, [rooms, currentUserId, conversationLabel, groupLabel, matrixClient, _])

  const filteredSummaries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return roomSummaries
    return roomSummaries.filter(summary => {
      const name = summary.displayName.toLowerCase()
      const preview = summary.lastMessagePreview.toLowerCase()
      return name.includes(query) || preview.includes(query)
    })
  }, [roomSummaries, searchQuery])

  const roomSummaryById = useMemo(() => {
    const map = new Map<string, (typeof roomSummaries)[number]>()
    roomSummaries.forEach(summary => {
      if (summary.roomId) {
        map.set(summary.roomId, summary)
      }
    })
    return map
  }, [roomSummaries])

  const onMatrixIdChange = useCallback(
    (value: string) => {
      setUsername(value)
      const looksLikeEmail =
        value.includes('@') && !value.trim().startsWith('@')
      if (!looksLikeEmail) {
        setEmailHelp(false)
      }
      if (hasCustomHomeserver) return
      const derived = deriveHomeserverFromMatrixId(value)
      if (derived) {
        setHomeserverInput(derived)
        setConfigError(null)
      }
    },
    [hasCustomHomeserver],
  )

  const onHomeserverChange = useCallback((value: string) => {
    setHomeserverInput(value)
    setHasCustomHomeserver(true)
    setConfigError(null)
  }, [])

  const roomIdFromParams = (route?.params as any)?.roomId as string | undefined
  const roomIdFromLegacy = (route?.params as any)?.conversation as
    | string
    | undefined

  const getRoomIdFromLocation = useCallback(() => {
    if (typeof window === 'undefined') return null
    const path = window.location.pathname || ''
    const parts = path.split('/').filter(Boolean)
    if (parts[0] === 'chat' && parts[1]) {
      return decodeURIComponent(parts[1])
    }
    const params = new URLSearchParams(window.location.search)
    return params.get('room')
  }, [])

  const updateRoomPath = useCallback((roomId: string | null, replace?: boolean) => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.pathname = roomId ? `/chat/${encodeURIComponent(roomId)}` : '/chat'
    url.searchParams.delete('room')
    const nextUrl = url.toString()
    const currentUrl = window.location.href
    if (nextUrl === currentUrl) return
    if (replace) {
      window.history.replaceState({}, '', nextUrl)
    } else {
      window.history.pushState({}, '', nextUrl)
    }
  }, [])

  const openNewGroup = useCallback(() => {
    setNewGroupOpen(true)
    setNewConversationOpen(false)
  }, [])

  const onCreateGroup = useCallback(async () => {
    if (status !== 'ready') return
    const name = newGroupName.trim()
    if (!name || isCreatingGroup) return
    setIsCreatingGroup(true)
    setError(null)
    try {
      const topic = newGroupDescription.trim()
      const roomId = await createGroupRoom({
        name,
        topic: topic || undefined,
      })
      setNewGroupName('')
      setNewGroupDescription('')
      setNewGroupOpen(false)
      refreshRooms()
      if (roomId) {
        setSelectedRoomId(roomId)
        setGroupPanelOpen(true)
        setInviteInput('')
        setInviteError(null)
        setLeaveError(null)
        updateRoomPath(roomId)
      }
    } catch (err) {
      const message = cleanError(err) || _(msg`Impossible de créer le groupe.`)
      setError(message)
    } finally {
      setIsCreatingGroup(false)
    }
  }, [
    status,
    newGroupName,
    newGroupDescription,
    isCreatingGroup,
    refreshRooms,
    updateRoomPath,
    _,
  ])

  const onBackToList = useCallback(() => {
    setSelectedRoomId(null)
    setRoomMessages([])
    setMessagesError(null)
    setSendError(null)
    setMessageInput('')
    setContextMenu(null)
    setReplyPreview(null)
    setEditTarget(null)
    setCameraMenuOpen(false)
    setAttachmentMenuOpen(false)
    setEmojiPickerOpen(false)
    setEmojiPickerMode(null)
    setReactionTarget(null)
    setForwardTarget(null)
    setForwardOpen(false)
    setSettingsOpen(false)
    setGroupPanelOpen(false)
    setInviteInput('')
    setInviteError(null)
    setLeaveError(null)
    updateRoomPath(null, true)
  }, [updateRoomPath])

  const openNewConversation = useCallback(() => {
    setNewConversationOpen(true)
    setNewGroupOpen(false)
  }, [])

  const selectRoom = useCallback(
    (room: any) => {
      const roomId = room?.roomId
      if (!roomId) return
      setSelectedRoomId(roomId)
      setRoomMessages([])
      setMessagesError(null)
      setSendError(null)
      setMessageInput('')
      setContextMenu(null)
      setReplyPreview(null)
      setEditTarget(null)
      setCameraMenuOpen(false)
      setAttachmentMenuOpen(false)
      setEmojiPickerOpen(false)
      setEmojiPickerMode(null)
      setReactionTarget(null)
      setForwardTarget(null)
      setForwardOpen(false)
      setSettingsOpen(false)
      setGroupPanelOpen(false)
      setInviteInput('')
      setInviteError(null)
      setLeaveError(null)
      updateRoomPath(roomId)
    },
    [updateRoomPath],
  )

  const loadRoomMessages = useCallback(
    async (roomId: string) => {
      if (!roomId) return
      const client = getClient()
      if (!client?.getRoom) {
        setMessagesError(_(msg`Tellus Chat indisponible.`))
        return
      }
      setMessagesLoading(true)
      setMessagesError(null)
      try {
        const room = client.getRoom(roomId)
        if (!room) {
          throw new Error('Conversation introuvable')
        }
        const timeline = room.getLiveTimeline?.()
        const unfilteredTimeline = room.getUnfilteredTimeline?.()
        const events =
          timeline?.getEvents?.() ??
          unfilteredTimeline?.getEvents?.() ??
          room.timeline ??
          []
        const currentUserId = session?.userId || client.getUserId?.() || ''
        const {messages, reactionIndex} = buildRoomMessages(
          events,
          currentUserId,
          client,
        )
        if (latestRoomId.current !== roomId) return
        reactionIndexRef.current = reactionIndex
        setRoomMessages(messages)
      } catch (err) {
        if (latestRoomId.current !== roomId) return
        const message =
          cleanError(err) || _(msg`Impossible de charger les messages.`)
        setMessagesError(message)
      } finally {
        if (latestRoomId.current === roomId) {
          setMessagesLoading(false)
        }
      }
    },
    [session?.userId, _],
  )

  const onRetryMessages = useCallback(() => {
    if (!selectedRoomId) return
    loadRoomMessages(selectedRoomId)
  }, [selectedRoomId, loadRoomMessages])

  const scrollToBottom = useCallback((animated: boolean) => {
    requestAnimationFrame(() => {
      messageListRef.current?.scrollToEnd({animated})
    })
  }, [])

  const showUnavailable = useCallback((message: string) => {
    Toast.show(message)
    logger.info('Tellus Chat unavailable action', {message})
  }, [])

  const canSendEvent = useCallback(
    (room: any, eventType: string) => {
      if (!room) return false
      const userId = session?.userId || getClient()?.getUserId?.() || ''
      const allowed = room?.currentState?.maySendEvent?.(eventType, userId)
      return allowed !== false
    },
    [session?.userId],
  )

  const updateMessageById = useCallback(
    (messageId: string, updater: (message: RoomMessage) => RoomMessage) => {
      setRoomMessages(prev => {
        const index = prev.findIndex(message => message.id === messageId)
        if (index === -1) return prev
        const next = [...prev]
        next[index] = updater(prev[index])
        return next
      })
    },
    [],
  )

  const replaceReactionId = useCallback(
    (messageId: string, emoji: string, oldId: string, newId: string) => {
      updateMessageById(messageId, message => {
        const reactions = {...(message.reactions || {})}
        const existing = reactions[emoji]
        if (!existing) return message
        const myReactionIds = existing.myReactionIds.map(id =>
          id === oldId ? newId : id,
        )
        reactions[emoji] = {
          ...existing,
          myReactionIds,
          me: myReactionIds.length > 0,
        }
        return {
          ...message,
          reactions,
        }
      })
    },
    [updateMessageById],
  )

  const addOrUpdateMessage = useCallback((message: RoomMessage) => {
    setRoomMessages(prev => {
      const index = prev.findIndex(item => item.id === message.id)
      if (index !== -1) {
        const next = [...prev]
        next[index] = {...prev[index], ...message}
        return next
      }
      if (!prev.length || message.ts >= prev[prev.length - 1].ts) {
        return [...prev, message]
      }
      return [...prev, message].sort((a, b) => a.ts - b.ts)
    })
  }, [])

  const onSendMessage = useCallback(async () => {
    if (!selectedRoomId || !messageInput.trim() || isSending) return
    const body = messageInput.trim()
    const client = getClient()
    const room = client?.getRoom?.(selectedRoomId)
    if (!canSendEvent(room, 'm.room.message')) {
      showUnavailable('Indisponible dans ce salon.')
      return
    }
    if (
      editTarget &&
      (!editTarget.isMine ||
        editTarget.msgtype !== 'm.text' ||
        editTarget.deleted)
    ) {
      showUnavailable('Impossible de modifier ce message.')
      setEditTarget(null)
      return
    }
    setIsSending(true)
    setSendError(null)
    setMessageInput('')
    try {
      const currentUserId = session?.userId || client.getUserId?.() || ''
      if (editTarget) {
        await sendTextMessage(selectedRoomId, body, {editOf: editTarget.id})
        updateMessageById(editTarget.id, message =>
          applyEditToMessage(message, body),
        )
      } else {
        const response = await sendTextMessage(selectedRoomId, body, {
          replyTo: replyPreview?.id,
        })
        const eventId = response?.event_id || response?.eventId
        const optimistic: RoomMessage = {
          id: String(eventId || `local-${Date.now()}`),
          sender: currentUserId,
          body,
          ts: Date.now(),
          isMine: true,
          msgtype: 'm.text',
          replyTo: replyPreview?.id || null,
          status: 'sent',
        }
        addOrUpdateMessage(optimistic)
      }
      setReplyPreview(null)
      setEditTarget(null)
      scrollToBottom(true)
    } catch (err) {
      const message =
        cleanError(err) || _(msg`Impossible d'envoyer le message.`)
      setSendError(message)
      setMessageInput(body)
      Toast.show(message)
    } finally {
      setIsSending(false)
    }
  }, [
    selectedRoomId,
    messageInput,
    isSending,
    session?.userId,
    replyPreview?.id,
    editTarget,
    scrollToBottom,
    _,
    canSendEvent,
    showUnavailable,
    updateMessageById,
    addOrUpdateMessage,
  ])

  const replaceMessageId = useCallback(
    (oldId: string, newId: string, extra?: Partial<RoomMessage>) => {
      setRoomMessages(prev => {
        const existingIndex = prev.findIndex(message => message.id === newId)
        const targetIndex = prev.findIndex(message => message.id === oldId)
        if (targetIndex === -1) return prev
        const next = [...prev]
        if (existingIndex !== -1) {
          next.splice(targetIndex, 1)
          return next
        }
        next[targetIndex] = {
          ...next[targetIndex],
          id: newId,
          ...extra,
        }
        return next
      })
    },
    [],
  )

  const blobFromUri = useCallback(async (uri: string) => {
    const response = await fetch(uri)
    return await response.blob()
  }, [])

  const pickWebFiles = useCallback(
    async ({
      accept,
      multiple,
    }: {
      accept?: string
      multiple?: boolean
    }) => {
      if (!isWeb || typeof document === 'undefined') return []
      return await new Promise<any[]>(resolve => {
        const input = document.createElement('input')
        input.type = 'file'
        if (accept) input.accept = accept
        if (multiple) input.multiple = multiple
        input.onchange = () => {
          const files = Array.from(input.files || [])
          resolve(files)
        }
        input.click()
      })
    },
    [],
  )

  const sendMediaMessage = useCallback(
    async ({
      blob,
      name,
      mimeType,
      msgtype,
      info,
    }: {
      blob: Blob
      name: string
      mimeType: string
      msgtype: string
      info?: Record<string, any>
    }) => {
      if (!selectedRoomId) return
      const client = getClient()
      const room = client?.getRoom?.(selectedRoomId)
      if (!canSendEvent(room, 'm.room.message')) {
        showUnavailable('Indisponible dans ce salon.')
        return
      }
      const currentUserId = session?.userId || client.getUserId?.() || ''
      const localId = `upload-${Date.now()}`
      const optimistic: RoomMessage = {
        id: localId,
        sender: currentUserId,
        body: name || defaultBodyForMsgtype(msgtype),
        ts: Date.now(),
        isMine: true,
        msgtype,
        fileName: name,
        mediaMimeType: mimeType,
        status: 'sending',
      }
      addOrUpdateMessage(optimistic)
      pendingUploadsRef.current.set(localId, {
        roomId: selectedRoomId,
        messageId: localId,
        file: blob,
        name,
        mimeType,
        msgtype,
        info,
        replyTo: replyPreview?.id || null,
      })

      try {
        const upload = await uploadMediaContent(blob, {name, mimeType})
        const mxcUrl = upload?.content_uri || upload?.contentUri
        const response = await sendMedia(selectedRoomId, null, {
          msgtype,
          name,
          mimeType,
          info,
          url: mxcUrl,
          replyTo: replyPreview?.id,
        })
        const eventId = response?.event_id || response?.eventId
        const mediaHttpUrl =
          mxcUrl && typeof client?.mxcUrlToHttp === 'function'
            ? client.mxcUrlToHttp(mxcUrl)
            : null
        if (eventId) {
          replaceMessageId(localId, String(eventId), {
            status: 'sent',
            mediaUrl: mxcUrl || null,
            mediaHttpUrl,
          })
        } else {
          updateMessageById(localId, message => ({
            ...message,
            status: 'sent',
            mediaUrl: mxcUrl || null,
            mediaHttpUrl,
          }))
        }
        pendingUploadsRef.current.delete(localId)
        scrollToBottom(true)
        setReplyPreview(null)
        setEditTarget(null)
      } catch (err) {
        const message =
          cleanError(err) || _(msg`Impossible d'envoyer le média.`)
        Toast.show(message)
        updateMessageById(localId, messageItem => ({
          ...messageItem,
          status: 'failed',
        }))
      }
    },
    [
      selectedRoomId,
      session?.userId,
      addOrUpdateMessage,
      updateMessageById,
      replaceMessageId,
      scrollToBottom,
      canSendEvent,
      showUnavailable,
      _,
      replyPreview?.id,
      setReplyPreview,
      setEditTarget,
    ],
  )

  const handleMediaCandidate = useCallback(
    async ({
      uri,
      file,
      name,
      mimeType,
      size,
      width,
      height,
    }: {
      uri?: string
      file?: Blob | null
      name?: string | null
      mimeType?: string | null
      size?: number | null
      width?: number | null
      height?: number | null
    }) => {
      try {
        let blob: Blob | null = null
        if (file instanceof Blob) {
          blob = file
        } else if (uri) {
          blob = await blobFromUri(uri)
        }
        if (!blob) {
          Toast.show(_(msg`Impossible de charger ce fichier.`))
          return
        }
        const resolvedMime = mimeType || blob.type || 'application/octet-stream'
        const msgtype = guessMsgtypeFromMime(resolvedMime)
        const fileName = (file as any)?.name
        const safeName = name || fileName || defaultBodyForMsgtype(msgtype)
        const info: Record<string, any> = {}
        if (resolvedMime) info.mimetype = resolvedMime
        if (typeof size === 'number') info.size = size
        if (typeof width === 'number') info.w = width
        if (typeof height === 'number') info.h = height
        await sendMediaMessage({
          blob,
          name: safeName,
          mimeType: resolvedMime,
          msgtype,
          info,
        })
      } catch (err) {
        const message =
          cleanError(err) || _(msg`Impossible d'envoyer le média.`)
        Toast.show(message)
      }
    },
    [blobFromUri, sendMediaMessage, _],
  )

  const onPickFromGallery = useCallback(async () => {
    try {
      const result = await openUnifiedPicker({selectionCountRemaining: 1})
      const asset = result?.assets?.[0]
      if (!asset) return
      await handleMediaCandidate({
        uri: asset.uri,
        name: asset.fileName,
        mimeType: asset.mimeType,
        size: asset.fileSize,
        width: asset.width,
        height: asset.height,
      })
    } catch (err) {
      const message =
        cleanError(err) || _(msg`Impossible d'ouvrir la galerie.`)
      Toast.show(message)
    }
  }, [handleMediaCandidate, _])

  const onPickFromCamera = useCallback(async () => {
    if (isWeb) {
      const files = await pickWebFiles({
        accept: 'image/*,video/*',
        multiple: false,
      })
      const file = files[0]
      if (file) {
        await handleMediaCandidate({
          file,
          name: file.name,
          mimeType: file.type,
          size: file.size,
        })
      }
      return
    }
    try {
      const asset = await openCamera({
        quality: 1,
      })
      await handleMediaCandidate({
        uri: asset.path,
        name: asset.path?.split('/').pop(),
        mimeType: asset.mime,
        size: asset.size,
        width: asset.width,
        height: asset.height,
      })
    } catch (err) {
      const message =
        cleanError(err) || _(msg`Impossible d'ouvrir la caméra.`)
      Toast.show(message)
    }
  }, [handleMediaCandidate, pickWebFiles, _])

  const onPickFile = useCallback(async () => {
    if (!isWeb) {
      showUnavailable('Fichiers indisponibles sur cet appareil.')
      return
    }
    const files = await pickWebFiles({multiple: false})
    const file = files[0]
    if (!file) return
    await handleMediaCandidate({
      file,
      name: file.name,
      mimeType: file.type,
      size: file.size,
    })
  }, [handleMediaCandidate, pickWebFiles, showUnavailable])

  const onRetryUpload = useCallback(
    async (message: RoomMessage) => {
      const pending = pendingUploadsRef.current.get(message.id)
      if (!pending) {
        Toast.show(_(msg`Impossible de réessayer cet envoi.`))
        return
      }
      updateMessageById(message.id, item => ({
        ...item,
        status: 'sending',
      }))
      try {
        const upload = await uploadMediaContent(pending.file, {
          name: pending.name,
          mimeType: pending.mimeType,
        })
        const mxcUrl = upload?.content_uri || upload?.contentUri
        const response = await sendMedia(pending.roomId, null, {
          msgtype: pending.msgtype,
          name: pending.name,
          mimeType: pending.mimeType,
          info: pending.info,
          url: mxcUrl,
          replyTo: pending.replyTo,
        })
        const eventId = response?.event_id || response?.eventId
        const client = getClient()
        const mediaHttpUrl =
          mxcUrl && typeof client?.mxcUrlToHttp === 'function'
            ? client.mxcUrlToHttp(mxcUrl)
            : null
        if (eventId) {
          replaceMessageId(message.id, String(eventId), {
            status: 'sent',
            mediaUrl: mxcUrl || null,
            mediaHttpUrl,
          })
        } else {
          updateMessageById(message.id, item => ({
            ...item,
            status: 'sent',
            mediaUrl: mxcUrl || null,
            mediaHttpUrl,
          }))
        }
        pendingUploadsRef.current.delete(message.id)
      } catch (err) {
        const errorMessage =
          cleanError(err) || _(msg`Impossible d'envoyer le média.`)
        Toast.show(errorMessage)
        updateMessageById(message.id, item => ({
          ...item,
          status: 'failed',
        }))
      }
    },
    [updateMessageById, replaceMessageId, _],
  )

  const toggleReaction = useCallback(
    async (message: RoomMessage, emoji: string) => {
      if (!selectedRoomId) return
      const client = getClient()
      const room = client?.getRoom?.(selectedRoomId)
      if (!canSendEvent(room, 'm.reaction')) {
        showUnavailable('Réactions indisponibles dans ce salon.')
        return
      }
      const currentUserId = session?.userId || client.getUserId?.() || ''
      const existing = message.reactions?.[emoji]?.myReactionIds?.[0]
      if (existing) {
        updateMessageById(message.id, item =>
          removeReactionFromMessage(
            item,
            emoji,
            currentUserId,
            existing,
            currentUserId,
          ),
        )
        try {
          await redactEvent(selectedRoomId, existing, 'Remove reaction')
          reactionIndexRef.current.delete(existing)
        } catch (err) {
          updateMessageById(message.id, item =>
            addReactionToMessage(
              item,
              emoji,
              currentUserId,
              existing,
              currentUserId,
            ),
          )
          Toast.show(_(msg`Impossible de retirer la réaction.`))
        }
        return
      }

      const localId = `local-reaction-${Date.now()}`
      updateMessageById(message.id, item =>
        addReactionToMessage(
          item,
          emoji,
          currentUserId,
          localId,
          currentUserId,
        ),
      )
      try {
        const response = await sendReaction(selectedRoomId, message.id, emoji)
        const eventId = response?.event_id || response?.eventId
        if (eventId) {
          replaceReactionId(message.id, emoji, localId, String(eventId))
          reactionIndexRef.current.set(String(eventId), {
            targetId: message.id,
            emoji,
            sender: currentUserId,
            eventId: String(eventId),
          })
        }
      } catch (err) {
        updateMessageById(message.id, item =>
          removeReactionFromMessage(
            item,
            emoji,
            currentUserId,
            localId,
            currentUserId,
          ),
        )
        Toast.show(_(msg`Impossible d'ajouter la réaction.`))
      }
    },
    [
      selectedRoomId,
      session?.userId,
      updateMessageById,
      replaceReactionId,
      canSendEvent,
      showUnavailable,
      _,
    ],
  )

  const onEmojiSelected = useCallback(
    (emoji: string) => {
      if (emojiPickerMode === 'reaction' && reactionTarget) {
        toggleReaction(reactionTarget, emoji)
      } else {
        setMessageInput(prev => `${prev}${emoji}`)
      }
      setEmojiPickerOpen(false)
      setEmojiPickerMode(null)
      setReactionTarget(null)
    },
    [emojiPickerMode, reactionTarget, toggleReaction],
  )

  const openEmojiPickerForComposer = useCallback(() => {
    setEmojiPickerMode('compose')
    setReactionTarget(null)
    setEmojiPickerOpen(true)
  }, [])

  const openEmojiPickerForReaction = useCallback((message: RoomMessage) => {
    setReactionTarget(message)
    setEmojiPickerMode('reaction')
    setEmojiPickerOpen(true)
  }, [])

  const onPressCamera = useCallback(() => {
    if (isWeb) {
      onPickFromCamera()
      return
    }
    setCameraMenuOpen(true)
  }, [onPickFromCamera])

  const onPressImage = useCallback(() => {
    onPickFromGallery()
  }, [onPickFromGallery])

  const onPressPlus = useCallback(() => {
    setAttachmentMenuOpen(true)
  }, [])

  const onPressVoice = useCallback(() => {
    showUnavailable('Messages vocaux bientôt.')
  }, [showUnavailable])

  const onAudioCall = useCallback(() => {
    showUnavailable('Appel audio bientôt.')
  }, [showUnavailable])

  const onVideoCall = useCallback(() => {
    showUnavailable('Appel vidéo bientôt.')
  }, [showUnavailable])

  const openMessageMenu = useCallback(
    (message: RoomMessage, event?: any) => {
      const cancelable =
        event?.cancelable ?? event?.nativeEvent?.cancelable ?? false
      if (cancelable) {
        event?.preventDefault?.()
      }
      event?.stopPropagation?.()
      const native = event?.nativeEvent ?? event
      const fallbackX =
        typeof window !== 'undefined' ? window.innerWidth / 2 : 0
      const fallbackY =
        typeof window !== 'undefined' ? window.innerHeight / 2 : 0
      const x = native?.clientX ?? native?.pageX ?? fallbackX
      const y = native?.clientY ?? native?.pageY ?? fallbackY
      setContextMenu({message, x, y})
    },
    [],
  )

  const closeMessageMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  const onReplyToMessage = useCallback((message: RoomMessage) => {
    setReplyPreview(message)
    setEditTarget(null)
    setContextMenu(null)
  }, [])

  const onEditMessage = useCallback((message: RoomMessage) => {
    if (!message.isMine || message.msgtype !== 'm.text' || message.deleted) {
      Toast.show(_(msg`Impossible de modifier ce message.`))
      setContextMenu(null)
      return
    }
    setEditTarget(message)
    setReplyPreview(null)
    setMessageInput(message.body)
    setContextMenu(null)
  }, [_])

  const clearReplyPreview = useCallback(() => {
    setReplyPreview(null)
  }, [])

  const clearEditTarget = useCallback(() => {
    setEditTarget(null)
  }, [])

  const onCopyMessage = useCallback(
    async (message: RoomMessage) => {
      const text =
        message.msgtype === 'm.text'
          ? message.body
          : message.mediaHttpUrl || message.mediaUrl || message.body
      if (!text) {
        Toast.show(_(msg`Rien à copier.`))
        return
      }
      await setStringAsync(text)
      Toast.show(_(msg`Copié dans le presse-papiers.`))
    },
    [_],
  )

  const onForwardMessage = useCallback((message: RoomMessage) => {
    setForwardTarget(message)
    setForwardOpen(true)
    setContextMenu(null)
  }, [])

  const onDeleteMessage = useCallback(
    (message: RoomMessage) => {
      if (!selectedRoomId) return
      if (!message.isMine) {
        showUnavailable('Suppression indisponible pour ce message.')
        return
      }
      const room = getClient()?.getRoom?.(selectedRoomId)
      if (!canSendEvent(room, 'm.room.redaction')) {
        showUnavailable('Suppression indisponible dans ce salon.')
        return
      }
      Alert.alert(
        'Supprimer le message',
        'Cette action est irréversible.',
        [
          {text: 'Annuler', style: 'cancel'},
          {
            text: 'Supprimer',
            style: 'destructive',
            onPress: async () => {
              updateMessageById(message.id, markMessageDeleted)
              try {
                await redactEvent(selectedRoomId, message.id, 'Delete message')
              } catch (err) {
                Toast.show(_(msg`Impossible de supprimer le message.`))
              }
            },
          },
        ],
      )
    },
    [selectedRoomId, updateMessageById, showUnavailable, canSendEvent, _],
  )

  const onConfirmForward = useCallback(
    async (room: any) => {
      if (!forwardTarget || !room?.roomId) return
      if (forwardTarget.deleted) {
        Toast.show(_(msg`Impossible de transférer ce message.`))
        return
      }
      if (!canSendEvent(room, 'm.room.message')) {
        showUnavailable('Transfert indisponible dans ce salon.')
        return
      }
      try {
        if (forwardTarget.msgtype === 'm.text') {
          await sendTextMessage(room.roomId, forwardTarget.body)
        } else if (forwardTarget.mediaUrl) {
          await sendMedia(room.roomId, null, {
            msgtype: forwardTarget.msgtype || 'm.file',
            name:
              forwardTarget.fileName ||
              forwardTarget.body ||
              defaultBodyForMsgtype(forwardTarget.msgtype || 'm.file'),
            mimeType: forwardTarget.mediaMimeType || undefined,
            url: forwardTarget.mediaUrl,
          })
        } else {
          Toast.show(_(msg`Impossible de transférer ce message.`))
          return
        }
        Toast.show(_(msg`Message transféré.`))
        setForwardOpen(false)
        setForwardTarget(null)
      } catch (err) {
        const message =
          cleanError(err) || _(msg`Impossible de transférer ce message.`)
        Toast.show(message)
      }
    },
    [forwardTarget, canSendEvent, showUnavailable, _],
  )

  const loadMyProfile = useCallback(async () => {
    if (!isReady) return
    const client = getClient()
    const userId = session?.userId || client?.getUserId?.() || ''
    if (!client || !userId || typeof client.getProfileInfo !== 'function') {
      return
    }
    setProfileLoading(true)
    try {
      const profile = await client.getProfileInfo(userId)
      const displayName = profile?.displayname || ''
      const avatarUrl = profile?.avatar_url || null
      const avatarHttp =
        avatarUrl && typeof client?.mxcUrlToHttp === 'function'
          ? client.mxcUrlToHttp(avatarUrl)
          : null
      setProfileDisplayName(displayName)
      setProfileAvatarUrl(avatarHttp)
    } catch (err) {
      logger.error('Tellus Chat profile load failed', {message: err})
    } finally {
      setProfileLoading(false)
    }
  }, [isReady, session?.userId])

  const openSettings = useCallback(() => {
    setSettingsOpen(true)
    loadMyProfile()
  }, [loadMyProfile])

  const closeSettings = useCallback(() => {
    setSettingsOpen(false)
  }, [])

  const onSaveDisplayName = useCallback(async () => {
    const value = profileDisplayName.trim()
    if (!value) {
      Toast.show(_(msg`Le nom d'affichage est requis.`))
      return
    }
    setProfileSaving(true)
    try {
      await updateMyMatrixProfile({displayName: value})
      Toast.show(_(msg`Nom d'affichage mis à jour.`))
      refreshRooms()
    } catch (err) {
      const message =
        cleanError(err) || _(msg`Impossible de mettre à jour le profil.`)
      Toast.show(message)
    } finally {
      setProfileSaving(false)
    }
  }, [profileDisplayName, refreshRooms, _])

  const onSelectAvatar = useCallback(async () => {
    try {
      let file: Blob | null = null
      if (isWeb) {
        const files = await pickWebFiles({
          accept: 'image/*',
          multiple: false,
        })
        file = files[0] || null
      } else {
        const asset = await openPicker()
        const picked = asset?.[0]
        if (picked?.path) {
          file = await blobFromUri(picked.path)
        }
      }
      if (!file) return
      setProfileSaving(true)
      const mxcUrl = await updateMyMatrixProfile({avatarFile: file})
      const client = getClient()
      const avatarHttp =
        mxcUrl && typeof client?.mxcUrlToHttp === 'function'
          ? client.mxcUrlToHttp(mxcUrl)
          : null
      setProfileAvatarUrl(avatarHttp || null)
      Toast.show(_(msg`Photo de profil mise à jour.`))
      refreshRooms()
    } catch (err) {
      const message =
        cleanError(err) || _(msg`Impossible de mettre à jour la photo.`)
      Toast.show(message)
    } finally {
      setProfileSaving(false)
    }
  }, [blobFromUri, pickWebFiles, refreshRooms, _])

  const showLoginForm = !isReady && status !== 'loading'
  const loginDisabled = isConnecting || !username.trim() || !password
  const showNewConversation = isReady && newConversationOpen
  const showNewGroup = isReady && newGroupOpen
  const isThreadView = Boolean(selectedRoomId)
  useEffect(() => {
    const shouldMinimizeShell = isMobile && isThreadView
    setMinimalShellMode(shouldMinimizeShell)
    return () => setMinimalShellMode(false)
  }, [isMobile, isThreadView, setMinimalShellMode])
  const roomsById = useMemo(() => {
    const map = new Map<string, any>()
    rooms.forEach(room => {
      const roomId = room?.roomId
      if (roomId) map.set(roomId, room)
    })
    return map
  }, [rooms])
  const selectedRoom = selectedRoomId ? roomsById.get(selectedRoomId) : null
  const selectedRoomSummary = selectedRoomId
    ? roomSummaryById.get(selectedRoomId)
    : null
  const selectedRoomIsGroup = selectedRoom ? !isDirectRoom(selectedRoom) : false
  const selectedRoomMembers = useMemo(() => {
    if (!selectedRoom) return []
    return getRoomMembers(selectedRoom)
  }, [selectedRoom])
  const selectedRoomTitle =
    selectedRoomSummary?.displayName ||
    selectedRoom?.name ||
    selectedRoom?.getName?.() ||
    conversationLabel
  const selectedRoomAvatarUrl = selectedRoomSummary?.avatarUrl || null
  const selectedRoomInitials = getInitials(selectedRoomTitle)
  const showSummary = incomingCount > 0 || outgoingCount > 0
  const canSend =
    Boolean(selectedRoomId && messageInput.trim()) && !isSending && isReady
  const toggleGroupPanel = useCallback(() => {
    setGroupPanelOpen(open => !open)
    setInviteError(null)
    setLeaveError(null)
  }, [])

  const onInviteToGroup = useCallback(async () => {
    if (!selectedRoomId || !selectedRoomIsGroup) return
    const value = inviteInput.trim()
    if (!value || isInviting) return
    const normalizedValue = normalizeMatrixIdentifier(value, homeserverInput)
    const validation = validateMatrixId(normalizedValue)
    if (!validation.ok) {
      setInviteError(_(msg`Identifiant Tellus Chat invalide.`))
      return
    }
    setInviteError(null)
    setIsInviting(true)
    try {
      await inviteToRoom(selectedRoomId, validation.normalized)
      setInviteInput('')
      refreshRooms()
    } catch (err) {
      const message =
        cleanError(err) || _(msg`Impossible d’inviter cette personne.`)
      setInviteError(message)
    } finally {
      setIsInviting(false)
    }
  }, [
    selectedRoomId,
    selectedRoomIsGroup,
    inviteInput,
    isInviting,
    homeserverInput,
    refreshRooms,
    _,
  ])

  const onLeaveGroup = useCallback(async () => {
    if (!selectedRoomId || !selectedRoomIsGroup) return
    setLeaveError(null)
    try {
      await leaveRoom(selectedRoomId)
      refreshRooms()
      onBackToList()
    } catch (err) {
      const message =
        cleanError(err) || _(msg`Impossible de quitter le groupe.`)
      setLeaveError(message)
    }
  }, [selectedRoomId, selectedRoomIsGroup, refreshRooms, onBackToList, _])

  useEffect(() => {
    const nextRoomId = roomIdFromParams || roomIdFromLegacy
    if (!isReady || !nextRoomId) return
    if (nextRoomId === selectedRoomId) return
    setSelectedRoomId(nextRoomId)
    setGroupPanelOpen(false)
    setInviteInput('')
    setInviteError(null)
    setLeaveError(null)
    setSettingsOpen(false)
    updateRoomPath(nextRoomId, true)
  }, [
    isReady,
    roomIdFromParams,
    roomIdFromLegacy,
    selectedRoomId,
    updateRoomPath,
  ])

  useEffect(() => {
    latestRoomId.current = selectedRoomId
    if (!selectedRoomId || status !== 'ready') {
      setMessagesLoading(false)
      setMessagesError(null)
      reactionIndexRef.current = new Map()
      pendingUploadsRef.current.clear()
      return
    }
    reactionIndexRef.current = new Map()
    pendingUploadsRef.current.clear()
    loadRoomMessages(selectedRoomId)
  }, [selectedRoomId, status, loadRoomMessages])

  useEffect(() => {
    if (!selectedRoomId || messagesLoading || roomMessages.length === 0) return
    scrollToBottom(false)
  }, [selectedRoomId, messagesLoading, roomMessages.length, scrollToBottom])

  useEffect(() => {
    if (!isReady || typeof window === 'undefined') return
    const roomId = getRoomIdFromLocation()
    if (roomId && !selectedRoomId) {
      setSelectedRoomId(roomId)
      setGroupPanelOpen(false)
      setInviteInput('')
      setInviteError(null)
      setLeaveError(null)
      setSettingsOpen(false)
    }
  }, [isReady, selectedRoomId, getRoomIdFromLocation])

  useEffect(() => {
    if (!isReady || typeof window === 'undefined') return
    const onPopState = () => {
      const roomId = getRoomIdFromLocation()
      if (!roomId) {
        setSelectedRoomId(null)
        setRoomMessages([])
        setMessagesError(null)
        setSendError(null)
        setContextMenu(null)
        setReplyPreview(null)
        setEditTarget(null)
        setCameraMenuOpen(false)
        setAttachmentMenuOpen(false)
        setEmojiPickerOpen(false)
        setEmojiPickerMode(null)
        setReactionTarget(null)
        setForwardTarget(null)
        setForwardOpen(false)
        setSettingsOpen(false)
        setGroupPanelOpen(false)
        setInviteInput('')
        setInviteError(null)
        setLeaveError(null)
        return
      }
      setSelectedRoomId(roomId)
      setGroupPanelOpen(false)
      setInviteInput('')
      setInviteError(null)
      setLeaveError(null)
      setSettingsOpen(false)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [isReady, getRoomIdFromLocation])

  useEffect(() => {
    if (!isReady || !selectedRoomId) return
    const client = getClient()
    if (!client?.on) return
    const onTimeline = (event: any, room: any) => {
      const roomId =
        room?.roomId || event?.getRoomId?.() || event?.event?.room_id
      if (!roomId || roomId !== selectedRoomId) return
      const currentUserId = session?.userId || client.getUserId?.() || ''
      const type = getEventType(event)
      if (type === 'm.room.message') {
        const content = getEventContent(event)
        const relatesTo = content?.['m.relates_to']
        if (relatesTo?.rel_type === 'm.replace') {
          const targetId = relatesTo?.event_id
          const nextBody =
            content?.['m.new_content']?.body || content?.body || ''
          if (targetId) {
            updateMessageById(targetId, message =>
              applyEditToMessage(message, nextBody),
            )
          }
          return
        }
        const parsed = formatMatrixMessage(event, currentUserId, client)
        if (!parsed) return
        addOrUpdateMessage(parsed)
        refreshRooms()
        return
      }
      if (type === 'm.reaction') {
        const content = getEventContent(event)
        const relatesTo = content?.['m.relates_to']
        if (relatesTo?.rel_type !== 'm.annotation') return
        const targetId = relatesTo?.event_id
        const emoji = relatesTo?.key
        const reactionEventId = getEventId(event)
        const sender = getEventSender(event)
        if (!targetId || !emoji || !reactionEventId) return
        reactionIndexRef.current.set(String(reactionEventId), {
          targetId,
          emoji,
          sender,
          eventId: String(reactionEventId),
        })
        updateMessageById(targetId, message =>
          addReactionToMessage(
            message,
            emoji,
            sender,
            String(reactionEventId),
            currentUserId,
          ),
        )
        return
      }
      if (type === 'm.room.redaction') {
        const redacts =
          event?.event?.redacts ??
          event?.getRedacts?.() ??
          getEventContent(event)?.redacts
        if (!redacts) return
        const reaction = reactionIndexRef.current.get(String(redacts))
        if (reaction) {
          updateMessageById(reaction.targetId, message =>
            removeReactionFromMessage(
              message,
              reaction.emoji,
              reaction.sender,
              String(redacts),
              currentUserId,
            ),
          )
          reactionIndexRef.current.delete(String(redacts))
          return
        }
        updateMessageById(String(redacts), markMessageDeleted)
      }
    }
    client.on('Room.timeline', onTimeline)
    return () => client.removeListener?.('Room.timeline', onTimeline)
  }, [
    isReady,
    selectedRoomId,
    session?.userId,
    refreshRooms,
    updateMessageById,
    addOrUpdateMessage,
  ])

  if (!isReady) {
    return (
      <PageShell title={_(msg`Tellus Chat`)}>
        <ScrollView contentContainerStyle={[a.gap_lg, styles.contentContainer]}>
          <View style={[styles.column, a.gap_lg]}>
            {error ? (
              <StateCard
                title={_(msg`Tellus Chat indisponible`)}
                description={error}
                icon={CircleInfoIcon}
                tone="error"
                action={
                  <Button
                    label={_(msg`Réessayer`)}
                    color="secondary"
                    size="small"
                    variant="solid"
                    onPress={onRetry}>
                    <ButtonText>
                      <Trans>Réessayer</Trans>
                    </ButtonText>
                    <ButtonIcon icon={RetryIcon} position="right" />
                  </Button>
                }
              />
            ) : null}

            {status === 'loading' ? (
              <StateCard
                title={_(msg`Chargement des messages`)}
                description={_(msg`Connexion à Tellus Chat en cours...`)}
                icon={CircleInfoIcon}
              />
            ) : null}

            {showLoginForm ? (
              <Card style={[styles.cardNarrow]}>
                <View style={[a.gap_md]}>
                  <Text style={[a.text_lg, a.font_bold]}>
                    <Trans>Se connecter à Tellus Chat</Trans>
                  </Text>
                  <View style={[a.gap_md]}>
                    <View>
                      <TextField.LabelText>
                        <Trans>Identifiant Tellus Chat</Trans>
                      </TextField.LabelText>
                      <TextField.Root>
                        <TextField.Input
                          label={_(msg`Pseudo ou e-mail`)}
                          value={username}
                          onChangeText={onMatrixIdChange}
                          autoCapitalize="none"
                          autoCorrect={false}
                        />
                      </TextField.Root>
                      {emailHelp ? (
                        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                          <Trans>
                            Utilisez votre identifiant @nom:domaine.
                          </Trans>
                        </Text>
                      ) : null}
                    </View>
                    <View>
                      <TextField.LabelText>
                        <Trans>Mot de passe</Trans>
                      </TextField.LabelText>
                      <TextField.Root>
                        <TextField.Input
                          label={_(msg`Mot de passe`)}
                          value={password}
                          onChangeText={setPassword}
                          secureTextEntry
                          autoCapitalize="none"
                          autoCorrect={false}
                        />
                      </TextField.Root>
                    </View>
                  </View>
                  <View style={[a.flex_row, a.align_center, a.justify_between]}>
                    <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                      <Switch
                        value={stayLoggedIn}
                        onValueChange={setStayLoggedIn}
                      />
                      <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                        <Trans>Rester connecté</Trans>
                      </Text>
                    </View>
                    <Button
                      label={_(msg`Se connecter`)}
                      color="primary"
                      size="small"
                      variant="solid"
                      disabled={loginDisabled}
                      onPress={onLogin}>
                      <ButtonText>
                        <Trans>Se connecter</Trans>
                      </ButtonText>
                    </Button>
                  </View>

                  <View style={[a.gap_sm]}>
                    <Button
                      label={_(msg`À propos / avancé`)}
                      color="secondary"
                      size="small"
                      variant="ghost"
                      onPress={() => setAdvancedOpen(open => !open)}>
                      <ButtonText>
                        <Trans>À propos / avancé</Trans>
                      </ButtonText>
                    </Button>

                    {advancedOpen ? (
                      <View style={[a.gap_sm]}>
                        <View style={[a.gap_2xs]}>
                          <Text style={[a.text_sm, a.font_bold]}>
                            <Trans>À propos</Trans>
                          </Text>
                          <Text
                            style={[a.text_sm, t.atoms.text_contrast_medium]}>
                            <Trans>Tellus Chat est basé sur Matrix.</Trans>
                          </Text>
                          <Text
                            style={[a.text_sm, t.atoms.text_contrast_medium]}>
                            <Trans>
                              Connecte ton compte Tellus Chat (propulsé par
                              Matrix).
                            </Trans>
                          </Text>
                        </View>
                        <View>
                          <TextField.LabelText>
                            <Trans>Serveur</Trans>
                          </TextField.LabelText>
                          <TextField.Root>
                            <TextField.Input
                              label={_(msg`https://matrix.org`)}
                              value={homeserverInput}
                              onChangeText={onHomeserverChange}
                              autoCapitalize="none"
                              autoCorrect={false}
                            />
                          </TextField.Root>
                          <Text
                            style={[a.text_sm, t.atoms.text_contrast_medium]}>
                            <Trans>
                              Change seulement si tu utilises un autre serveur.
                            </Trans>
                          </Text>
                          {configError ? (
                            <Text
                              style={[
                                a.text_sm,
                                styles.errorText,
                                {color: t.palette.negative_500},
                              ]}>
                              {configError}
                            </Text>
                          ) : null}
                        </View>
                      </View>
                    ) : null}
                  </View>
                </View>
              </Card>
            ) : null}

            {status === 'connecting' ? (
              <StateCard
                title={_(msg`Connexion à Tellus Chat`)}
                description={_(msg`Synchronisation des conversations...`)}
                icon={CircleInfoIcon}
              />
            ) : null}
          </View>
        </ScrollView>
      </PageShell>
    )
  }

  return (
    <Layout.Screen style={[styles.screen]}>
      <View
        style={[
          styles.appFrame,
          {maxWidth: isMobile ? 480 : 560},
        ]}>
        {isThreadView ? (
          settingsOpen ? (
            <ChatSettingsPage
              displayName={profileDisplayName}
              avatarUrl={profileAvatarUrl}
              isLoading={profileLoading}
              isSaving={profileSaving}
              onBack={closeSettings}
              onChangeDisplayName={setProfileDisplayName}
              onSaveDisplayName={onSaveDisplayName}
              onSelectAvatar={onSelectAvatar}
            />
          ) : (
            <ChatThreadPage
              isMobile={isMobile}
              roomId={selectedRoomId}
              title={selectedRoomTitle}
              subtitle={selectedRoomId ? displayMatrix(selectedRoomId) : ''}
              avatarUrl={selectedRoomAvatarUrl}
              initials={selectedRoomInitials}
              isGroup={selectedRoomIsGroup}
              onBack={onBackToList}
              onOpenSettings={openSettings}
              onAudioCall={onAudioCall}
              onVideoCall={onVideoCall}
              onToggleGroupPanel={toggleGroupPanel}
              groupPanelOpen={groupPanelOpen}
              groupPanelContent={
                selectedRoomId && selectedRoomIsGroup && groupPanelOpen ? (
                  <GroupPanel
                    members={selectedRoomMembers}
                    inviteInput={inviteInput}
                    inviteError={inviteError}
                    leaveError={leaveError}
                    isInviting={isInviting}
                    onInviteInputChange={value => {
                      setInviteInput(value)
                      if (inviteError) setInviteError(null)
                    }}
                    onInvite={onInviteToGroup}
                    onLeave={onLeaveGroup}
                  />
                ) : null
              }
              messages={roomMessages}
              messagesLoading={messagesLoading}
              messagesError={messagesError}
              onRetryMessages={onRetryMessages}
              matrixClient={matrixClient}
              selectedRoom={selectedRoom}
              messageListRef={messageListRef}
              onOpenMessageMenu={openMessageMenu}
              onRetryUpload={onRetryUpload}
              contextMenu={contextMenu}
              onCloseMenu={closeMessageMenu}
              onReply={onReplyToMessage}
              onEdit={onEditMessage}
              onCopy={onCopyMessage}
              onForward={onForwardMessage}
              onDelete={onDeleteMessage}
              onReact={toggleReaction}
              onOpenEmojiPicker={openEmojiPickerForReaction}
              replyPreview={replyPreview}
              editTarget={editTarget}
              onClearReply={clearReplyPreview}
              onClearEdit={clearEditTarget}
              sendError={sendError}
              messageInput={messageInput}
              onChangeMessageInput={setMessageInput}
              onSendMessage={onSendMessage}
              onPressCamera={onPressCamera}
              onPressVoice={onPressVoice}
              onPressImage={onPressImage}
              onPressEmoji={openEmojiPickerForComposer}
              onPressPlus={onPressPlus}
              canSend={canSend}
              isSending={isSending}
            />
          )
        ) : (
          <ConversationsPage
            title={_(msg`Tellus Chat`)}
            onNewConversation={openNewConversation}
            onNewGroup={openNewGroup}
            onLogout={hasSession ? onLogout : undefined}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            conversations={filteredSummaries}
            groupLabel={groupLabel}
            selectedRoomId={selectedRoomId}
            onSelectRoom={selectRoom}
            emptyState={
              <StateCard
                title={_(msg`Aucune discussion`)}
                description={_(
                  msg`Lance une discussion ou crée un groupe.`,
                )}
                icon={MessageIcon}
                tone="empty"
                action={
                  <Button
                    label={_(msg`Nouvelle discussion`)}
                    color="primary"
                    size="small"
                    variant="solid"
                    onPress={openNewConversation}>
                    <ButtonIcon icon={PlusIcon} position="left" />
                    <ButtonText>
                      <Trans>Nouvelle discussion</Trans>
                    </ButtonText>
                  </Button>
                }
              />
            }>
            {error ? (
              <StateCard
                title={_(msg`Tellus Chat indisponible`)}
                description={error}
                icon={CircleInfoIcon}
                tone="error"
                action={
                  <Button
                    label={_(msg`Réessayer`)}
                    color="secondary"
                    size="small"
                    variant="solid"
                    onPress={onRetry}>
                    <ButtonText>
                      <Trans>Réessayer</Trans>
                    </ButtonText>
                    <ButtonIcon icon={RetryIcon} position="right" />
                  </Button>
                }
              />
            ) : null}
            {showNewConversation ? (
              <Card style={styles.formCard}>
                <View style={[a.gap_md]}>
                  <Text style={[styles.formTitle]}>
                    <Trans>Nouvelle discussion</Trans>
                  </Text>
                  <View>
                    <TextField.LabelText>
                      <Trans>Identifiant Tellus Chat</Trans>
                    </TextField.LabelText>
                    <TextField.Root>
                      <TextField.Input
                        label={_(msg`Pseudo`)}
                        value={newConversationUser}
                        onChangeText={setNewConversationUser}
                        autoCapitalize="none"
                        autoCorrect={false}
                      />
                    </TextField.Root>
                  </View>
                  <View style={[a.flex_row, a.justify_between]}>
                    <Button
                      label={_(msg`Annuler`)}
                      color="secondary"
                      size="small"
                      variant="outline"
                      onPress={() => setNewConversationOpen(false)}>
                      <ButtonText>
                        <Trans>Annuler</Trans>
                      </ButtonText>
                    </Button>
                    <Button
                      label={_(msg`Créer`)}
                      color="primary"
                      size="small"
                      variant="solid"
                      disabled={!newConversationUser.trim() || isCreating}
                      onPress={onCreateConversation}>
                      <ButtonText>
                        <Trans>Créer</Trans>
                      </ButtonText>
                    </Button>
                  </View>
                </View>
              </Card>
            ) : null}
            {showNewGroup ? (
              <Card style={styles.formCard}>
                <View style={[a.gap_md]}>
                  <Text style={[styles.formTitle]}>
                    <Trans>Nouveau groupe</Trans>
                  </Text>
                  <View style={[a.gap_md]}>
                    <View>
                      <TextField.LabelText>
                        <Trans>Nom du groupe</Trans>
                      </TextField.LabelText>
                      <TextField.Root>
                        <TextField.Input
                          label={_(msg`Mon groupe`)}
                          value={newGroupName}
                          onChangeText={setNewGroupName}
                          autoCapitalize="sentences"
                          autoCorrect={false}
                        />
                      </TextField.Root>
                    </View>
                    <View>
                      <TextField.LabelText>
                        <Trans>Description (optionnelle)</Trans>
                      </TextField.LabelText>
                      <TextField.Root>
                        <TextField.Input
                          label={_(msg`De quoi parle ce groupe ?`)}
                          value={newGroupDescription}
                          onChangeText={setNewGroupDescription}
                          autoCapitalize="sentences"
                          autoCorrect={false}
                        />
                      </TextField.Root>
                    </View>
                  </View>
                  <View style={[a.flex_row, a.justify_between]}>
                    <Button
                      label={_(msg`Annuler`)}
                      color="secondary"
                      size="small"
                      variant="outline"
                      onPress={() => setNewGroupOpen(false)}>
                      <ButtonText>
                        <Trans>Annuler</Trans>
                      </ButtonText>
                    </Button>
                    <Button
                      label={_(msg`Créer`)}
                      color="primary"
                      size="small"
                      variant="solid"
                      disabled={!newGroupName.trim() || isCreatingGroup}
                      onPress={onCreateGroup}>
                      <ButtonText>
                        <Trans>Créer</Trans>
                      </ButtonText>
                    </Button>
                  </View>
                </View>
              </Card>
            ) : null}
            {showSummary ? (
              <View style={[styles.summaryRow]}>
                <Card style={[styles.summaryCard]}>
                  <View style={[a.gap_xs]}>
                    <Text style={[styles.summaryLabel]}>
                      <Trans>Invitations</Trans>
                    </Text>
                    <Text style={[styles.summaryValue]}>{incomingCount}</Text>
                  </View>
                </Card>
                <Card style={[styles.summaryCard]}>
                  <View style={[a.gap_xs]}>
                    <Text style={[styles.summaryLabel]}>
                      <Trans>En attente</Trans>
                    </Text>
                    <Text style={[styles.summaryValue]}>{outgoingCount}</Text>
                  </View>
                </Card>
              </View>
            ) : null}
          </ConversationsPage>
        )}
      </View>
      <ActionSheet
        open={cameraMenuOpen}
        title="Caméra"
        onClose={() => setCameraMenuOpen(false)}
        actions={[
          {
            label: 'Prendre une photo',
            onPress: () => {
              setCameraMenuOpen(false)
              onPickFromCamera()
            },
          },
          {
            label: 'Choisir dans la galerie',
            onPress: () => {
              setCameraMenuOpen(false)
              onPickFromGallery()
            },
          },
        ]}
      />
      <ActionSheet
        open={attachmentMenuOpen}
        title="Ajouter"
        onClose={() => setAttachmentMenuOpen(false)}
        actions={[
          {
            label: 'Photo / Vidéo',
            onPress: () => {
              setAttachmentMenuOpen(false)
              onPickFromGallery()
            },
          },
          ...(isWeb
            ? [
                {
                  label: 'Fichier',
                  onPress: () => {
                    setAttachmentMenuOpen(false)
                    onPickFile()
                  },
                },
              ]
            : []),
        ]}
      />
      <ForwardSheet
        open={forwardOpen}
        onClose={() => {
          setForwardOpen(false)
          setForwardTarget(null)
        }}
        conversations={roomSummaries}
        onSelect={onConfirmForward}
      />
      <EmojiPickerModal
        open={emojiPickerOpen}
        onClose={() => {
          setEmojiPickerOpen(false)
          setEmojiPickerMode(null)
          setReactionTarget(null)
        }}
        onSelect={onEmojiSelected}
      />
    </Layout.Screen>
  )
}

type RoomSummary = {
  room: any
  roomId: string | null
  displayName: string
  avatarUrl: string | null
  initials: string
  isGroup: boolean
  lastMessagePreview: string
  lastMessageTs: number | null
  unreadCount: number | null
}

function IconButton({
  icon: Icon,
  label,
  onPress,
  style,
  iconStyle,
}: {
  icon: ComponentType<any>
  label: string
  onPress?: () => void
  style?: any
  iconStyle?: any
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({pressed, hovered, focused}) => [
        styles.iconButton,
        hovered && styles.iconButtonHovered,
        pressed && styles.iconButtonPressed,
        focused && styles.iconButtonFocused,
        style,
      ]}>
      <Icon size="sm" style={[styles.icon, iconStyle]} />
    </Pressable>
  )
}

function ConversationsPage({
  title,
  onNewConversation,
  onNewGroup,
  onLogout,
  searchQuery,
  onSearchQueryChange,
  conversations,
  groupLabel,
  selectedRoomId,
  onSelectRoom,
  emptyState,
  children,
}: {
  title: string
  onNewConversation: () => void
  onNewGroup: () => void
  onLogout?: () => void
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  conversations: RoomSummary[]
  groupLabel: string
  selectedRoomId: string | null
  onSelectRoom: (room: any) => void
  emptyState: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <View style={styles.page}>
      <View style={styles.glassHeader}>
        <Text style={styles.headerTitle}>{title}</Text>
        <View style={styles.headerActions}>
          <IconButton
            label="Nouvelle discussion"
            icon={PlusIcon}
            onPress={onNewConversation}
          />
          <IconButton
            label="Nouveau groupe"
            icon={GroupIcon}
            onPress={onNewGroup}
          />
        </View>
      </View>

      <View style={styles.searchBar}>
        <SearchIcon size="sm" style={styles.iconSubtle} />
        <TextInput
          placeholder="Rechercher"
          value={searchQuery}
          onChangeText={onSearchQueryChange}
          placeholderTextColor={chatTokens.colors.textSubtle}
          style={styles.searchInput}
        />
      </View>

      <View style={styles.sectionStack}>{children}</View>

      <ScrollView
        style={styles.conversationList}
        contentContainerStyle={styles.conversationListContent}>
        {conversations.length === 0 ? (
          emptyState
        ) : (
          conversations.map(summary => {
            const {
              room,
              roomId,
              displayName,
              avatarUrl,
              initials,
              isGroup,
              lastMessagePreview,
              lastMessageTs,
              unreadCount,
            } = summary
            const isSelected = Boolean(roomId) && roomId === selectedRoomId
            const showUnreadBadge =
              typeof unreadCount === 'number' && unreadCount > 0
            return (
              <Pressable
                key={roomId || displayName}
                accessibilityRole="button"
                accessibilityLabel={displayName}
                accessibilityHint="Ouvrir la conversation"
                onPress={() => onSelectRoom(room)}
                style={({pressed, hovered, focused}) => [
                  styles.conversationItem,
                  hovered && styles.conversationItemHover,
                  pressed && styles.conversationItemPressed,
                  focused && styles.conversationItemFocused,
                  isSelected && styles.conversationItemSelected,
                ]}>
                <View style={styles.avatar}>
                  {avatarUrl && !isGroup ? (
                    <Image
                      source={{uri: avatarUrl}}
                      style={styles.avatarImage}
                      resizeMode="cover"
                      accessibilityIgnoresInvertColors
                    />
                  ) : isGroup ? (
                    <GroupIcon size="sm" style={styles.icon} />
                  ) : (
                    <Text style={styles.avatarInitials}>{initials}</Text>
                  )}
                </View>
                <View style={styles.conversationBody}>
                  <View style={styles.conversationRow}>
                    <View style={styles.conversationTitleRow}>
                      <Text style={styles.conversationTitle} numberOfLines={1}>
                        {displayName}
                      </Text>
                      {isGroup ? (
                        <View style={styles.groupBadge}>
                          <Text style={styles.groupBadgeText}>{groupLabel}</Text>
                        </View>
                      ) : null}
                    </View>
                    {lastMessageTs ? (
                      <TimeElapsed
                        timestamp={new Date(lastMessageTs).toISOString()}>
                        {({timeElapsed}) => (
                          <Text style={styles.conversationMeta}>
                            {timeElapsed}
                          </Text>
                        )}
                      </TimeElapsed>
                    ) : null}
                  </View>
                  <Text style={styles.conversationPreview} numberOfLines={1}>
                    {lastMessagePreview}
                  </Text>
                </View>
                <View style={styles.conversationMetaWrap}>
                  {showUnreadBadge ? (
                    <View style={styles.unreadBadge}>
                      <Text style={styles.unreadBadgeText}>{unreadCount}</Text>
                    </View>
                  ) : null}
                </View>
              </Pressable>
            )
          })
        )}
      </ScrollView>

      {onLogout ? (
        <View style={styles.logoutRow}>
          <Button
            label="Se déconnecter"
            color="secondary"
            size="small"
            variant="ghost"
            onPress={onLogout}>
            <ButtonText>
              <Trans>Se déconnecter</Trans>
            </ButtonText>
          </Button>
        </View>
      ) : null}
    </View>
  )
}

function ChatThreadPage({
  isMobile,
  roomId,
  title,
  subtitle,
  avatarUrl,
  initials,
  isGroup,
  onBack,
  onOpenSettings,
  onAudioCall,
  onVideoCall,
  onToggleGroupPanel,
  groupPanelOpen,
  groupPanelContent,
  messages,
  messagesLoading,
  messagesError,
  onRetryMessages,
  matrixClient,
  selectedRoom,
  messageListRef,
  onOpenMessageMenu,
  onRetryUpload,
  contextMenu,
  onCloseMenu,
  onReply,
  onEdit,
  onCopy,
  onForward,
  onDelete,
  onReact,
  onOpenEmojiPicker,
  replyPreview,
  editTarget,
  onClearReply,
  onClearEdit,
  sendError,
  messageInput,
  onChangeMessageInput,
  onSendMessage,
  onPressCamera,
  onPressVoice,
  onPressImage,
  onPressEmoji,
  onPressPlus,
  canSend,
  isSending,
}: {
  isMobile: boolean
  roomId: string | null
  title: string
  subtitle: string
  avatarUrl: string | null
  initials: string
  isGroup: boolean
  onBack: () => void
  onOpenSettings: () => void
  onAudioCall: () => void
  onVideoCall: () => void
  onToggleGroupPanel: () => void
  groupPanelOpen: boolean
  groupPanelContent: React.ReactNode
  messages: RoomMessage[]
  messagesLoading: boolean
  messagesError: string | null
  onRetryMessages: () => void
  matrixClient: any
  selectedRoom: any
  messageListRef: React.MutableRefObject<ScrollView | null>
  onOpenMessageMenu: (message: RoomMessage, event?: any) => void
  onRetryUpload: (message: RoomMessage) => void
  contextMenu: MessageContextState | null
  onCloseMenu: () => void
  onReply: (message: RoomMessage) => void
  onEdit: (message: RoomMessage) => void
  onCopy: (message: RoomMessage) => void
  onForward: (message: RoomMessage) => void
  onDelete: (message: RoomMessage) => void
  onReact: (message: RoomMessage, emoji: string) => void
  onOpenEmojiPicker: (message: RoomMessage) => void
  replyPreview: RoomMessage | null
  editTarget: RoomMessage | null
  onClearReply: () => void
  onClearEdit: () => void
  sendError: string | null
  messageInput: string
  onChangeMessageInput: (value: string) => void
  onSendMessage: () => void
  onPressCamera: () => void
  onPressVoice: () => void
  onPressImage: () => void
  onPressEmoji: () => void
  onPressPlus: () => void
  canSend: boolean
  isSending: boolean
}) {
  return (
    <View style={styles.chatPage}>
      <ChatHeader
        title={title}
        subtitle={subtitle}
        avatarUrl={avatarUrl}
        initials={initials}
        onBack={onBack}
        onOpenSettings={onOpenSettings}
        onAudioCall={onAudioCall}
        onVideoCall={onVideoCall}
      />

      {isGroup ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Voir membres"
          onPress={onToggleGroupPanel}
          style={({pressed}) => [
            styles.groupToggle,
            pressed && styles.groupTogglePressed,
          ]}>
          <GroupIcon size="sm" style={styles.iconSubtle} />
          <Text style={styles.groupToggleText}>
            {groupPanelOpen ? 'Masquer les membres' : 'Voir les membres'}
          </Text>
        </Pressable>
      ) : null}

      <View style={styles.threadBody}>
        {groupPanelContent}
        {!roomId ? (
          <StateCard
            title="Sélectionne une conversation"
            description="Choisis une discussion pour voir le fil."
            icon={MessageIcon}
            tone="empty"
          />
        ) : messagesLoading ? (
          <StateCard
            title="Chargement"
            description="Récupération des messages..."
            icon={CircleInfoIcon}
          />
        ) : messagesError ? (
          <StateCard
            title="Impossible de charger la conversation"
            description={messagesError}
            icon={CircleInfoIcon}
            tone="error"
            action={
              <Button
                label="Réessayer"
                color="secondary"
                size="small"
                variant="solid"
                onPress={onRetryMessages}>
                <ButtonText>
                  <Trans>Réessayer</Trans>
                </ButtonText>
                <ButtonIcon icon={RetryIcon} position="right" />
              </Button>
            }
          />
        ) : messages.length === 0 ? (
          <StateCard
            title="Aucun message"
            description="Commence la discussion."
            icon={MessageIcon}
            tone="empty"
          />
        ) : (
          <MessageList
            isMobile={isMobile}
            messages={messages}
            selectedRoom={selectedRoom}
            matrixClient={matrixClient}
            messageListRef={messageListRef}
            onOpenMessageMenu={onOpenMessageMenu}
            onRetryUpload={onRetryUpload}
          />
        )}
      </View>

      <Composer
        replyPreview={replyPreview}
        editTarget={editTarget}
        onClearReply={onClearReply}
        onClearEdit={onClearEdit}
        sendError={sendError}
        messageInput={messageInput}
        onChangeMessageInput={onChangeMessageInput}
        onSendMessage={onSendMessage}
        onPressCamera={onPressCamera}
        onPressVoice={onPressVoice}
        onPressImage={onPressImage}
        onPressEmoji={onPressEmoji}
        onPressPlus={onPressPlus}
        canSend={canSend}
        isSending={isSending}
      />

      <MessageContextMenu
        state={contextMenu}
        onClose={onCloseMenu}
        onReply={onReply}
        onEdit={onEdit}
        onCopy={onCopy}
        onForward={onForward}
        onDelete={onDelete}
        onReact={onReact}
        onOpenEmojiPicker={onOpenEmojiPicker}
      />
    </View>
  )
}

function ChatHeader({
  title,
  subtitle,
  avatarUrl,
  initials,
  onBack,
  onOpenSettings,
  onAudioCall,
  onVideoCall,
}: {
  title: string
  subtitle: string
  avatarUrl: string | null
  initials: string
  onBack: () => void
  onOpenSettings: () => void
  onAudioCall: () => void
  onVideoCall: () => void
}) {
  return (
    <View style={styles.chatHeader}>
      <IconButton label="Retour" icon={ChevronLeft} onPress={onBack} />
      <View style={styles.chatHeaderTitle}>
        <View style={styles.avatarSmall}>
          {avatarUrl ? (
            <Image
              source={{uri: avatarUrl}}
              style={styles.avatarImage}
              resizeMode="cover"
              accessibilityIgnoresInvertColors
            />
          ) : (
            <Text style={styles.avatarInitialsSmall}>{initials}</Text>
          )}
        </View>
        <View style={styles.chatHeaderText}>
          <Text style={styles.chatTitle} numberOfLines={1}>
            {title}
          </Text>
          <Text style={styles.chatSubtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        </View>
      </View>
      <View style={styles.headerActions}>
        <IconButton label="Paramètres" icon={SettingsIcon} onPress={onOpenSettings} />
        <IconButton label="Appel audio" icon={PhoneIcon} onPress={onAudioCall} />
        <IconButton label="Appel vidéo" icon={VideoIcon} onPress={onVideoCall} />
      </View>
    </View>
  )
}

function ChatSettingsPage({
  displayName,
  avatarUrl,
  isLoading,
  isSaving,
  onBack,
  onChangeDisplayName,
  onSaveDisplayName,
  onSelectAvatar,
}: {
  displayName: string
  avatarUrl: string | null
  isLoading: boolean
  isSaving: boolean
  onBack: () => void
  onChangeDisplayName: (value: string) => void
  onSaveDisplayName: () => void
  onSelectAvatar: () => void
}) {
  const initials = getInitials(displayName || 'Moi')
  return (
    <View style={styles.chatPage}>
      <View style={styles.chatHeader}>
        <IconButton label="Retour" icon={ChevronLeft} onPress={onBack} />
        <View style={styles.chatHeaderTitle}>
          <Text style={styles.chatTitle} numberOfLines={1}>
            Paramètres du chat
          </Text>
          <Text style={styles.chatSubtitle} numberOfLines={1}>
            Profil Matrix
          </Text>
        </View>
        <View style={styles.headerActions} />
      </View>

      <View style={styles.settingsBody}>
        <View style={styles.settingsCard}>
          <View style={styles.settingsAvatarRow}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Modifier la photo de profil"
              onPress={onSelectAvatar}
              style={styles.settingsAvatar}>
              {avatarUrl ? (
                <Image
                  source={{uri: avatarUrl}}
                  style={styles.avatarImage}
                  resizeMode="cover"
                  accessibilityIgnoresInvertColors
                />
              ) : (
                <Text style={styles.avatarInitials}>{initials}</Text>
              )}
            </Pressable>
            <View style={styles.settingsAvatarText}>
              <Text style={styles.settingsLabel}>Photo de profil Matrix</Text>
              <Button
                label="Changer"
                color="secondary"
                size="small"
                variant="outline"
                disabled={isSaving}
                onPress={onSelectAvatar}>
                <ButtonText>
                  <Trans>Changer</Trans>
                </ButtonText>
              </Button>
            </View>
          </View>

          <View style={styles.settingsField}>
            <Text style={styles.settingsLabel}>Nom d'affichage</Text>
            <TextInput
              value={displayName}
              onChangeText={onChangeDisplayName}
              placeholder="Votre nom"
              placeholderTextColor={chatTokens.colors.textSubtle}
              style={styles.settingsInput}
            />
            <Button
              label="Enregistrer"
              color="primary"
              size="small"
              variant="solid"
              disabled={isSaving || isLoading}
              onPress={onSaveDisplayName}>
              <ButtonText>
                <Trans>Enregistrer</Trans>
              </ButtonText>
            </Button>
            {isLoading ? (
              <Text style={styles.settingsHint}>Chargement du profil...</Text>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  )
}

function MessageList({
  isMobile,
  messages,
  selectedRoom,
  matrixClient,
  messageListRef,
  onOpenMessageMenu,
  onRetryUpload,
}: {
  isMobile: boolean
  messages: RoomMessage[]
  selectedRoom: any
  matrixClient: any
  messageListRef: React.MutableRefObject<ScrollView | null>
  onOpenMessageMenu: (message: RoomMessage, event?: any) => void
  onRetryUpload: (message: RoomMessage) => void
}) {
  const messageLookup = useMemo(() => {
    const map = new Map<string, RoomMessage>()
    messages.forEach(message => map.set(message.id, message))
    return map
  }, [messages])
  let lastDateKey = ''
  return (
    <ScrollView
      ref={messageListRef}
      style={styles.messageList}
      contentContainerStyle={styles.messageListContent}>
      {messages.map((message, index) => {
        const dateKey = new Date(message.ts).toDateString()
        const showDate = dateKey !== lastDateKey
        lastDateKey = dateKey
        const prev = messages[index - 1]
        const next = messages[index + 1]
        const groupedWithPrev = Boolean(prev && prev.sender === message.sender)
        const groupedWithNext = Boolean(next && next.sender === message.sender)
        const showAvatar = !message.isMine && !groupedWithPrev
        const showTime = !groupedWithNext
        const senderMember = selectedRoom?.getMember?.(message.sender)
        const senderName = getMemberDisplayName(senderMember)
        const senderInitials = getInitials(senderName || message.sender)
        const senderAvatarUrl = getMemberAvatarUrl(matrixClient, senderMember)
        return (
          <View key={message.id}>
            {showDate ? (
              <View style={styles.dateChip}>
                <Text style={styles.dateChipText}>
                  {formatChatDate(message.ts)}
                </Text>
              </View>
            ) : null}
            <MessageBubble
              message={message}
              isMobile={isMobile}
              showAvatar={showAvatar}
              avatarUrl={senderAvatarUrl}
              initials={senderInitials}
              groupedWithPrev={groupedWithPrev}
              showTime={showTime}
              onOpenMenu={onOpenMessageMenu}
              onRetryUpload={onRetryUpload}
              replyToMessage={
                message.replyTo ? messageLookup.get(message.replyTo) || null : null
              }
            />
          </View>
        )
      })}
    </ScrollView>
  )
}

function MessageBubble({
  message,
  isMobile,
  showAvatar,
  avatarUrl,
  initials,
  groupedWithPrev,
  showTime,
  onOpenMenu,
  onRetryUpload,
}: {
  message: RoomMessage
  isMobile: boolean
  showAvatar: boolean
  avatarUrl: string | null
  initials: string
  groupedWithPrev: boolean
  showTime: boolean
  onOpenMenu: (message: RoomMessage, event?: any) => void
  onRetryUpload: (message: RoomMessage) => void
}) {
  const showMeta =
    showTime || message.status === 'sending' || message.status === 'failed'
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressStartRef = useRef<{x: number; y: number} | null>(null)
  const longPressTriggeredRef = useRef(false)

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    longPressStartRef.current = null
  }, [])

  const getEventPoint = useCallback((event: any) => {
    const native = event?.nativeEvent ?? event
    const touch = native?.touches?.[0] ?? native?.changedTouches?.[0]
    if (touch) {
      return {
        x: touch.clientX ?? touch.pageX ?? 0,
        y: touch.clientY ?? touch.pageY ?? 0,
      }
    }
    return {
      x: native?.clientX ?? native?.pageX ?? 0,
      y: native?.clientY ?? native?.pageY ?? 0,
    }
  }, [])

  const triggerMenu = useCallback(
    (event?: any, point?: {x: number; y: number}) => {
      if (longPressTriggeredRef.current) return
      longPressTriggeredRef.current = true
      clearLongPressTimer()
      if (!event && point) {
        onOpenMenu(message, {clientX: point.x, clientY: point.y})
        return
      }
      onOpenMenu(message, event)
    },
    [clearLongPressTimer, message, onOpenMenu],
  )

  const startLongPress = useCallback(
    (event: any) => {
      if (longPressTimerRef.current) return
      longPressTriggeredRef.current = false
      const native = event?.nativeEvent ?? event
      const pointerType = native?.pointerType
      if (pointerType && pointerType !== 'touch') return
      if (!pointerType && !native?.touches && !native?.changedTouches) return
      const point = getEventPoint(event)
      longPressStartRef.current = point
      longPressTimerRef.current = setTimeout(() => {
        triggerMenu(undefined, point)
      }, LONG_PRESS_DELAY_MS)
    },
    [getEventPoint, triggerMenu],
  )

  const moveLongPress = useCallback(
    (event: any) => {
      if (!longPressTimerRef.current || !longPressStartRef.current) return
      const point = getEventPoint(event)
      const dx = point.x - longPressStartRef.current.x
      const dy = point.y - longPressStartRef.current.y
      if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_THRESHOLD) {
        clearLongPressTimer()
      }
    },
    [clearLongPressTimer, getEventPoint],
  )

  const endLongPress = useCallback(() => {
    clearLongPressTimer()
  }, [clearLongPressTimer])

  const handleLongPress = useCallback(
    (event: any) => {
      triggerMenu(event)
    },
    [triggerMenu],
  )

  const handleContextMenu = useCallback(
    (event: any) => {
      event?.stopPropagation?.()
      const cancelable =
        event?.cancelable ?? event?.nativeEvent?.cancelable ?? false
      if (cancelable) {
        event?.preventDefault?.()
      }
      triggerMenu(event)
    },
    [triggerMenu],
  )
  return (
    <View
      style={[
        styles.messageRow,
        message.isMine && styles.messageRowMine,
        {marginTop: groupedWithPrev ? 4 : 12},
      ]}>
      {!message.isMine && showAvatar ? (
        <View style={styles.avatarTiny}>
          {avatarUrl ? (
            <Image
              source={{uri: avatarUrl}}
              style={styles.avatarImage}
              resizeMode="cover"
              accessibilityIgnoresInvertColors
            />
          ) : (
            <Text style={styles.avatarInitialsTiny}>{initials}</Text>
          )}
        </View>
      ) : null}
      <View
        style={[
          styles.messageBubbleWrap,
          {maxWidth: isMobile ? '78%' : '68%'},
        ]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Message"
          onLongPress={handleLongPress}
          {...web({
            onContextMenu: handleContextMenu,
            onPointerDown: startLongPress,
            onPointerMove: moveLongPress,
            onPointerUp: endLongPress,
            onPointerCancel: endLongPress,
            onTouchStart: startLongPress,
            onTouchMove: moveLongPress,
            onTouchEnd: endLongPress,
            onTouchCancel: endLongPress,
          })}
          style={({pressed}) => [
            styles.messageBubble,
            message.isMine
              ? styles.messageBubbleMine
              : styles.messageBubbleThem,
            pressed && styles.messageBubblePressed,
            web({
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              touchAction: 'manipulation',
            }),
          ]}>
          <Text
            style={[
              styles.messageText,
              message.isMine && styles.messageTextMine,
              message.deleted && styles.messageTextDeleted,
            ]}>
            {message.body}
          </Text>
        </Pressable>
        {showMeta ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Statut du message"
            onPress={() => {
              if (message.status === 'failed') {
                onRetryUpload(message)
              }
            }}
            style={({pressed}) => [
              pressed && message.status === 'failed' && styles.retryPressed,
            ]}>
            <Text
              style={[
                styles.messageTime,
                message.isMine ? styles.messageTimeMine : null,
                message.status === 'failed' && styles.messageTimeError,
              ]}>
              {message.status === 'sending'
                ? 'Envoi…'
                : message.status === 'failed'
                  ? 'Échec · Réessayer'
                  : `${formatChatTime(message.ts)}${
                      message.edited ? ' · modifié' : ''
                    }`}
            </Text>
          </Pressable>
        ) : null}
        {message.reactions && Object.keys(message.reactions).length > 0 ? (
          <View style={styles.reactionSummaryRow}>
            {Object.entries(message.reactions).map(([emoji, data]) => (
              <View
                key={emoji}
                style={[
                  styles.reactionChip,
                  data.me && styles.reactionChipSelected,
                ]}>
                <Text style={styles.reactionChipText}>{emoji}</Text>
                <Text style={styles.reactionChipCount}>{data.count}</Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </View>
  )
}

function Composer({
  replyPreview,
  editTarget,
  onClearReply,
  onClearEdit,
  sendError,
  messageInput,
  onChangeMessageInput,
  onSendMessage,
  onPressCamera,
  onPressVoice,
  onPressImage,
  onPressEmoji,
  onPressPlus,
  canSend,
  isSending,
}: {
  replyPreview: RoomMessage | null
  editTarget: RoomMessage | null
  onClearReply: () => void
  onClearEdit: () => void
  sendError: string | null
  messageInput: string
  onChangeMessageInput: (value: string) => void
  onSendMessage: () => void
  onPressCamera: () => void
  onPressVoice: () => void
  onPressImage: () => void
  onPressEmoji: () => void
  onPressPlus: () => void
  canSend: boolean
  isSending: boolean
}) {
  const showSend = messageInput.trim().length > 0
  return (
    <View style={styles.composerWrap}>
      {replyPreview ? (
        <View style={styles.replyPreview}>
          <View style={styles.previewHeader}>
            <Text style={styles.replyLabel}>Répondre à</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Annuler la réponse"
              onPress={onClearReply}>
              <Text style={styles.previewAction}>Annuler</Text>
            </Pressable>
          </View>
          <Text style={styles.replyBody} numberOfLines={2}>
            {replyPreview.body}
          </Text>
        </View>
      ) : null}
      {editTarget ? (
        <View style={styles.editBanner}>
          <View style={styles.previewHeader}>
            <Text style={styles.editLabel}>Modifier le message</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Terminer l'édition"
              onPress={onClearEdit}>
              <Text style={styles.previewAction}>Terminé</Text>
            </Pressable>
          </View>
        </View>
      ) : null}
      {sendError ? (
        <Text style={styles.composerError}>{sendError}</Text>
      ) : null}
      <View style={styles.composerRow}>
        <IconButton
          label="Caméra"
          icon={CameraIcon}
          style={styles.iconSolid}
          iconStyle={styles.sendIcon}
          onPress={onPressCamera}
        />
        <TextInput
          placeholder="Message…"
          value={messageInput}
          onChangeText={onChangeMessageInput}
          style={styles.composerInput}
          placeholderTextColor={chatTokens.colors.textSubtle}
          editable={!isSending}
          autoCapitalize="sentences"
          autoCorrect
          multiline
          blurOnSubmit={false}
          returnKeyType="send"
          onKeyPress={(event: any) => {
            const key = event?.nativeEvent?.key
            const shiftKey = event?.nativeEvent?.shiftKey
            if (key === 'Enter' && !shiftKey) {
              const cancelable =
                event?.cancelable ?? event?.nativeEvent?.cancelable ?? false
              if (cancelable) {
                event?.preventDefault?.()
              }
              onSendMessage()
            }
          }}
        />
        <View style={styles.composerActions}>
          <IconButton label="Micro" icon={VoiceIcon} onPress={onPressVoice} />
          <IconButton label="Image" icon={ImageIcon} onPress={onPressImage} />
          <IconButton label="Emoji" icon={EmojiIcon} onPress={onPressEmoji} />
          <IconButton label="Plus" icon={PlusIcon} onPress={onPressPlus} />
          {showSend ? (
            <IconButton
              label="Envoyer"
              icon={SendIcon}
              onPress={canSend ? onSendMessage : undefined}
              style={[styles.sendButton, !canSend && styles.sendButtonDisabled]}
              iconStyle={styles.sendIcon}
            />
          ) : null}
        </View>
      </View>
    </View>
  )
}

function MessageContextMenu({
  state,
  onClose,
  onReply,
  onEdit,
  onReact,
  onOpenEmojiPicker,
  onCopy,
  onForward,
  onDelete,
}: {
  state: MessageContextState | null
  onClose: () => void
  onReply: (message: RoomMessage) => void
  onEdit: (message: RoomMessage) => void
  onReact: (message: RoomMessage, emoji: string) => void
  onOpenEmojiPicker: (message: RoomMessage) => void
  onCopy: (message: RoomMessage) => void
  onForward: (message: RoomMessage) => void
  onDelete: (message: RoomMessage) => void
}) {
  if (!state) return null
  const {message, x, y} = state
  const actions = [
    {label: 'Répondre', onPress: () => onReply(message)},
    ...(message.isMine && !message.deleted && message.msgtype === 'm.text'
      ? [{label: 'Modifier', onPress: () => onEdit(message)}]
      : []),
    {
      label: 'Copier',
      onPress: () => {
        onCopy(message)
        onClose()
      },
    },
    {
      label: 'Transférer',
      onPress: () => {
        onForward(message)
        onClose()
      },
    },
    {
      label: 'Supprimer',
      onPress: () => {
        onDelete(message)
        onClose()
      },
      tone: 'danger',
    },
  ]
  const menuWidth = 220
  const menuHeight = actions.length * 44 + 8
  const reactionHeight = 44
  const padding = 12
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : menuWidth
  const viewportHeight =
    typeof window !== 'undefined' ? window.innerHeight : menuHeight
  const safeX = Math.min(
    Math.max(padding, x),
    viewportWidth - menuWidth - padding,
  )
  const safeY = Math.min(
    Math.max(padding, y),
    viewportHeight - menuHeight - reactionHeight - padding,
  )

  return (
    <View style={styles.contextOverlay}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Fermer le menu"
        onPress={onClose}
        style={styles.contextOverlay}
      />
      <View style={[styles.contextMenuWrap, {top: safeY, left: safeX}]}>
        <View style={styles.reactionRow}>
          {['❤️', '👍', '😂', '😮', '😢', '😡'].map(reaction => {
            const isSelected =
              message.reactions?.[reaction]?.myReactionIds?.length > 0
            return (
              <Pressable
                key={reaction}
                accessibilityRole="button"
                accessibilityLabel={`Réaction ${reaction}`}
                onPress={() => {
                  onReact(message, reaction)
                  onClose()
                }}
                style={({pressed}) => [
                  styles.reactionButton,
                  pressed && styles.reactionButtonPressed,
                  isSelected && styles.reactionButtonSelected,
                ]}>
                <Text style={styles.reactionText}>{reaction}</Text>
              </Pressable>
            )
          })}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Ajouter une réaction"
            onPress={() => {
              onOpenEmojiPicker(message)
              onClose()
            }}
            style={({pressed}) => [
              styles.reactionButton,
              pressed && styles.reactionButtonPressed,
            ]}>
            <Text style={styles.reactionText}>+</Text>
          </Pressable>
        </View>
        <View style={styles.contextMenu}>
          {actions.map(action => (
            <Pressable
              key={action.label}
              accessibilityRole="button"
              accessibilityLabel={action.label}
              onPress={action.onPress}
              style={({pressed}) => [
                styles.contextMenuItem,
                pressed && styles.contextMenuItemPressed,
              ]}>
              <Text
                style={[
                  styles.contextMenuItemText,
                  action.tone === 'danger' && styles.contextMenuItemDanger,
                ]}>
                {action.label}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </View>
  )
}

function GroupPanel({
  members,
  inviteInput,
  inviteError,
  leaveError,
  isInviting,
  onInviteInputChange,
  onInvite,
  onLeave,
}: {
  members: any[]
  inviteInput: string
  inviteError: string | null
  leaveError: string | null
  isInviting: boolean
  onInviteInputChange: (value: string) => void
  onInvite: () => void
  onLeave: () => void
}) {
  return (
    <View style={styles.groupPanelCard}>
      <Text style={styles.groupPanelTitle}>
        <Trans>Membres du groupe</Trans>
      </Text>
      {members.length > 0 ? (
        <View style={styles.groupMembersList}>
          {members.map((member, index) => {
            const name =
              member?.name ||
              member?.rawDisplayName ||
              displayMatrix(member?.userId || '') ||
              ''
            const userId = member?.userId || ''
            return (
              <View
                key={userId || name || String(index)}
                style={styles.groupMemberRow}>
                <Text style={styles.groupMemberName} numberOfLines={1}>
                  {name}
                </Text>
                {userId ? (
                  <Text style={styles.groupMemberId} numberOfLines={1}>
                    {displayMatrix(userId)}
                  </Text>
                ) : null}
              </View>
            )
          })}
        </View>
      ) : (
        <Text style={styles.groupMemberEmpty}>
          <Trans>Aucun membre pour le moment.</Trans>
        </Text>
      )}

      <View style={styles.groupInviteSection}>
        <Text style={styles.groupInviteTitle}>
          <Trans>Inviter un membre</Trans>
        </Text>
        <TextField.Root>
          <TextField.Input
            label="Pseudo"
            value={inviteInput}
            onChangeText={onInviteInputChange}
            autoCapitalize="none"
            autoCorrect={false}
          />
        </TextField.Root>
        {inviteError ? (
          <Text style={styles.groupError}>{inviteError}</Text>
        ) : null}
        <View style={styles.groupInviteActions}>
          <Button
            label="Inviter"
            color="primary"
            size="small"
            variant="solid"
            disabled={!inviteInput.trim() || isInviting}
            onPress={onInvite}>
            <ButtonText>
              <Trans>Inviter</Trans>
            </ButtonText>
          </Button>
          <Button
            label="Quitter le groupe"
            color="secondary"
            size="small"
            variant="outline"
            onPress={onLeave}>
            <ButtonText>
              <Trans>Quitter le groupe</Trans>
            </ButtonText>
          </Button>
        </View>
        {leaveError ? <Text style={styles.groupError}>{leaveError}</Text> : null}
      </View>
    </View>
  )
}

function ActionSheet({
  open,
  title,
  actions,
  onClose,
}: {
  open: boolean
  title?: string
  actions: {label: string; onPress: () => void; tone?: 'danger'}[]
  onClose: () => void
}) {
  if (!open) return null
  return (
    <Modal transparent animationType="fade" visible={open} onRequestClose={onClose}>
      <View style={styles.sheetOverlay}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Fermer"
          onPress={onClose}
          style={styles.sheetBackdrop}
        />
        <View style={styles.sheetContainer}>
          <View style={styles.sheetCard}>
            {title ? <Text style={styles.sheetTitle}>{title}</Text> : null}
            {actions.map(action => (
              <Pressable
                key={action.label}
                accessibilityRole="button"
                accessibilityLabel={action.label}
                onPress={action.onPress}
                style={({pressed}) => [
                  styles.sheetItem,
                  pressed && styles.sheetItemPressed,
                ]}>
                <Text
                  style={[
                    styles.sheetItemText,
                    action.tone === 'danger' && styles.sheetItemDanger,
                  ]}>
                  {action.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  )
}

function ForwardSheet({
  open,
  onClose,
  conversations,
  onSelect,
}: {
  open: boolean
  onClose: () => void
  conversations: RoomSummary[]
  onSelect: (room: any) => void
}) {
  if (!open) return null
  return (
    <Modal transparent animationType="fade" visible={open} onRequestClose={onClose}>
      <View style={styles.sheetOverlay}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Fermer"
          onPress={onClose}
          style={styles.sheetBackdrop}
        />
        <View style={styles.forwardContainer}>
          <View style={styles.sheetCard}>
            <Text style={styles.sheetTitle}>Choisir une conversation</Text>
            <ScrollView
              style={styles.forwardList}
              contentContainerStyle={styles.forwardListContent}>
              {conversations.map(summary => (
                <Pressable
                  key={summary.roomId || summary.displayName}
                  accessibilityRole="button"
                  accessibilityLabel={summary.displayName}
                  onPress={() => {
                    onSelect(summary.room)
                    onClose()
                  }}
                  style={({pressed}) => [
                    styles.forwardItem,
                    pressed && styles.forwardItemPressed,
                  ]}>
                  <View style={styles.avatarTiny}>
                    {summary.avatarUrl ? (
                      <Image
                        source={{uri: summary.avatarUrl}}
                        style={styles.avatarImage}
                        resizeMode="cover"
                        accessibilityIgnoresInvertColors
                      />
                    ) : summary.isGroup ? (
                      <GroupIcon size="sm" style={styles.icon} />
                    ) : (
                      <Text style={styles.avatarInitialsTiny}>
                        {summary.initials}
                      </Text>
                    )}
                  </View>
                  <View style={styles.forwardItemText}>
                    <Text style={styles.forwardItemTitle} numberOfLines={1}>
                      {summary.displayName}
                    </Text>
                    <Text style={styles.forwardItemSubtitle} numberOfLines={1}>
                      {summary.lastMessagePreview}
                    </Text>
                  </View>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        </View>
      </View>
    </Modal>
  )
}

function EmojiPickerModal({
  open,
  onClose,
  onSelect,
}: {
  open: boolean
  onClose: () => void
  onSelect: (emoji: string) => void
}) {
  if (!open) return null
  const WebEmojiPicker = isWeb ? require('@emoji-mart/react').default : null
  const NativeEmojiPicker = !isWeb
    ? require('../../modules/expo-emoji-picker').EmojiPicker
    : null
  return (
    <Modal transparent animationType="fade" visible={open} onRequestClose={onClose}>
      <View style={styles.sheetOverlay}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Fermer"
          onPress={onClose}
          style={styles.sheetBackdrop}
        />
        <View style={styles.emojiPickerContainer}>
          <View style={styles.emojiPickerCard}>
            {isWeb && WebEmojiPicker ? (
              <WebEmojiPicker
                onEmojiSelect={(emoji: any) => {
                  if (emoji?.native) {
                    onSelect(emoji.native)
                  }
                }}
                autoFocus
              />
            ) : NativeEmojiPicker ? (
              <NativeEmojiPicker onEmojiSelected={onSelect} />
            ) : null}
          </View>
        </View>
      </View>
    </Modal>
  )
}

export default MessagesScreen

const styles = StyleSheet.create({
  contentContainer: {
    alignItems: 'center',
    paddingBottom: 48,
  },
  column: {
    width: '100%',
    maxWidth: 900,
  },
  cardNarrow: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
  },
  errorText: {
    paddingTop: 8,
  },
  screen: {
    flex: 1,
    backgroundColor: chatTokens.colors.bg,
  },
  appFrame: {
    width: '100%',
    alignSelf: 'center',
    flex: 1,
    minHeight: 0,
    paddingHorizontal: chatTokens.space[4],
    paddingTop: chatTokens.space[3],
    paddingBottom: chatTokens.space[4],
    gap: chatTokens.space[4],
    ...web({marginLeft: 'auto', marginRight: 'auto'}),
  },
  page: {
    flex: 1,
    minHeight: 0,
    gap: chatTokens.space[4],
  },
  glassHeader: {
    height: 56,
    borderRadius: chatTokens.radius.lg,
    paddingHorizontal: chatTokens.space[3],
    paddingVertical: chatTokens.space[2],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...glassSurface,
    ...web({position: 'sticky', top: 8, zIndex: 20}),
  },
  headerTitle: {
    fontSize: chatTokens.type.title,
    fontWeight: chatTokens.type.weightBold,
    color: chatTokens.colors.text,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[2],
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.85)',
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
    ...web({cursor: 'pointer'}),
  },
  iconButtonHovered: {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  iconButtonPressed: {
    transform: [{scale: 0.97}],
  },
  iconButtonFocused: web({
    outlineStyle: 'solid',
    outlineWidth: 2,
    outlineColor: 'rgba(109,92,255,0.35)',
  }),
  icon: {
    color: chatTokens.colors.icon,
  },
  iconSubtle: {
    color: chatTokens.colors.iconSubtle,
  },
  iconSolid: {
    backgroundColor: chatTokens.colors.bubbleMe,
    borderColor: 'rgba(109,92,255,0.35)',
  },
  searchBar: {
    height: 40,
    borderRadius: 20,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
    paddingHorizontal: chatTokens.space[3],
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[2],
  },
  searchInput: {
    flex: 1,
    fontSize: chatTokens.type.base,
    color: chatTokens.colors.text,
    paddingVertical: 0,
  },
  sectionStack: {
    gap: chatTokens.space[3],
  },
  conversationList: {
    flex: 1,
    minHeight: 0,
  },
  conversationListContent: {
    paddingBottom: chatTokens.space[4],
    gap: chatTokens.space[3],
  },
  conversationItem: {
    minHeight: 72,
    padding: chatTokens.space[3],
    borderRadius: chatTokens.radius.lg,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[3],
    ...web({cursor: 'pointer'}),
  },
  conversationItemHover: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderColor: 'rgba(0,0,0,0.12)',
  },
  conversationItemPressed: {
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  conversationItemFocused: web({
    outlineStyle: 'solid',
    outlineWidth: 2,
    outlineColor: 'rgba(109,92,255,0.35)',
  }),
  conversationItemSelected: {
    borderColor: chatTokens.colors.bubbleMe,
    backgroundColor: 'rgba(109,92,255,0.08)',
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  avatarSmall: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  avatarTiny: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarInitials: {
    fontSize: 13,
    fontWeight: '600',
    color: chatTokens.colors.text,
  },
  avatarInitialsSmall: {
    fontSize: 11,
    fontWeight: '600',
    color: chatTokens.colors.text,
  },
  avatarInitialsTiny: {
    fontSize: 10,
    fontWeight: '600',
    color: chatTokens.colors.text,
  },
  conversationBody: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  conversationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: chatTokens.space[2],
  },
  conversationTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[2],
    flex: 1,
    minWidth: 0,
  },
  conversationTitle: {
    fontSize: 16,
    fontWeight: chatTokens.type.weightBold,
    color: chatTokens.colors.text,
    flex: 1,
    minWidth: 0,
  },
  conversationMeta: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  conversationPreview: {
    fontSize: 13,
    color: chatTokens.colors.textSubtle,
  },
  conversationMetaWrap: {
    minWidth: 24,
    alignItems: 'flex-end',
  },
  unreadBadge: {
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: chatTokens.colors.bubbleMe,
  },
  unreadBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
  },
  logoutRow: {
    paddingTop: chatTokens.space[2],
  },
  formCard: {
    borderRadius: chatTokens.radius.lg,
  },
  formTitle: {
    fontSize: chatTokens.type.title,
    fontWeight: chatTokens.type.weightBold,
    color: chatTokens.colors.text,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: chatTokens.space[3],
  },
  summaryCard: {
    flex: 1,
  },
  summaryLabel: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: '600',
    color: chatTokens.colors.text,
  },
  chatPage: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    gap: chatTokens.space[3],
  },
  chatHeader: {
    height: 56,
    borderRadius: chatTokens.radius.lg,
    paddingHorizontal: chatTokens.space[3],
    paddingVertical: chatTokens.space[2],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...glassSurface,
    ...web({position: 'sticky', top: 8, zIndex: 20}),
  },
  chatHeaderTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[2],
    flex: 1,
    minWidth: 0,
  },
  chatHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  chatTitle: {
    fontSize: 16,
    fontWeight: chatTokens.type.weightBold,
    color: chatTokens.colors.text,
  },
  chatSubtitle: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  groupBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: chatTokens.colors.textSubtle,
  },
  groupBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  groupToggle: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[2],
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.04)',
    ...web({cursor: 'pointer'}),
  },
  groupTogglePressed: {
    backgroundColor: 'rgba(0,0,0,0.08)',
  },
  groupToggleText: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  threadBody: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: chatTokens.space[3],
    paddingTop: chatTokens.space[2],
  },
  messageList: {
    flex: 1,
    minHeight: 0,
  },
  messageListContent: {
    paddingBottom: 96,
  },
  dateChip: {
    alignSelf: 'center',
    backgroundColor: chatTokens.colors.chip,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    marginVertical: chatTokens.space[2],
  },
  dateChipText: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  messageRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: chatTokens.space[2],
  },
  messageRowMine: {
    justifyContent: 'flex-end',
  },
  messageBubbleWrap: {
    flexShrink: 1,
    minWidth: 0,
  },
  messageBubble: {
    borderRadius: chatTokens.radius.xl,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  messageBubbleMine: {
    backgroundColor: chatTokens.colors.bubbleMe,
    borderColor: 'transparent',
  },
  messageBubbleThem: {
    backgroundColor: chatTokens.colors.bubbleThem,
    borderColor: 'rgba(0,0,0,0.06)',
  },
  messageBubblePressed: {
    opacity: 0.9,
  },
  messageText: {
    fontSize: chatTokens.type.base,
    lineHeight: 20,
    color: chatTokens.colors.bubbleThemText,
  },
  messageTextMine: {
    color: chatTokens.colors.bubbleMeText,
  },
  messageTextDeleted: {
    color: chatTokens.colors.textSubtle,
    fontStyle: 'italic',
  },
  messageTime: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
    marginTop: 4,
  },
  messageTimeMine: {
    textAlign: 'right',
  },
  messageTimeError: {
    color: '#C13645',
  },
  retryPressed: {
    opacity: 0.8,
  },
  reactionSummaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 6,
  },
  reactionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  reactionChipSelected: {
    backgroundColor: 'rgba(109,92,255,0.12)',
  },
  reactionChipText: {
    fontSize: 14,
  },
  reactionChipCount: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  composerWrap: {
    borderRadius: 24,
    padding: 10,
    gap: chatTokens.space[2],
    ...glassSurface,
    ...web({
      position: 'sticky',
      bottom: 'calc(env(safe-area-inset-bottom) + 8px)',
    }),
  },
  replyPreview: {
    borderRadius: chatTokens.radius.md,
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
    padding: chatTokens.space[2],
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: chatTokens.space[2],
  },
  previewAction: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.bubbleMe,
    fontWeight: '600',
    ...web({cursor: 'pointer'}),
  },
  replyLabel: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  replyBody: {
    fontSize: chatTokens.type.base,
    color: chatTokens.colors.text,
  },
  editBanner: {
    borderRadius: chatTokens.radius.md,
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
    padding: chatTokens.space[2],
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
  editLabel: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  composerError: {
    fontSize: chatTokens.type.small,
    color: '#C13645',
  },
  composerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[2],
  },
  composerInput: {
    flex: 1,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.9)',
    paddingHorizontal: 12,
    fontSize: chatTokens.type.base,
    color: chatTokens.colors.text,
  },
  composerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[2],
  },
  sendButton: {
    backgroundColor: chatTokens.colors.bubbleMe,
    borderColor: 'rgba(109,92,255,0.4)',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
  sendIcon: {
    color: '#fff',
  },
  contextOverlay: {
    ...web({position: 'fixed'}),
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 50,
  },
  contextMenuWrap: {
    position: 'absolute',
  },
  reactionRow: {
    flexDirection: 'row',
    gap: chatTokens.space[2],
    padding: chatTokens.space[2],
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
    marginBottom: chatTokens.space[2],
  },
  reactionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.03)',
    ...web({cursor: 'pointer'}),
  },
  reactionButtonPressed: {
    backgroundColor: 'rgba(0,0,0,0.08)',
  },
  reactionButtonSelected: {
    backgroundColor: 'rgba(109,92,255,0.18)',
  },
  reactionText: {
    fontSize: 16,
  },
  contextMenu: {
    width: 220,
    borderRadius: chatTokens.radius.lg,
    paddingVertical: 4,
    overflow: 'hidden',
    ...glassSurface,
  },
  contextMenuItem: {
    height: 44,
    paddingHorizontal: 14,
    alignItems: 'flex-start',
    justifyContent: 'center',
    ...web({cursor: 'pointer'}),
  },
  contextMenuItemPressed: {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  contextMenuItemText: {
    fontSize: 14,
    color: chatTokens.colors.text,
    textAlign: 'left',
  },
  contextMenuItemDanger: {
    color: '#C13645',
  },
  groupPanelCard: {
    borderRadius: chatTokens.radius.lg,
    padding: chatTokens.space[3],
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
    backgroundColor: '#fff',
    marginBottom: chatTokens.space[3],
    gap: chatTokens.space[2],
  },
  groupPanelTitle: {
    fontSize: chatTokens.type.title,
    fontWeight: chatTokens.type.weightBold,
    color: chatTokens.colors.text,
  },
  groupMembersList: {
    gap: 6,
  },
  groupMemberRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: chatTokens.space[2],
  },
  groupMemberName: {
    fontSize: chatTokens.type.base,
    color: chatTokens.colors.text,
    flex: 1,
    minWidth: 0,
  },
  groupMemberId: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  groupMemberEmpty: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  groupInviteSection: {
    gap: chatTokens.space[2],
  },
  groupInviteTitle: {
    fontSize: chatTokens.type.small,
    fontWeight: chatTokens.type.weightBold,
    color: chatTokens.colors.text,
  },
  groupInviteActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: chatTokens.space[2],
  },
  groupError: {
    fontSize: chatTokens.type.small,
    color: '#C13645',
  },
  settingsBody: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: chatTokens.space[3],
    paddingTop: chatTokens.space[2],
  },
  settingsCard: {
    borderRadius: chatTokens.radius.lg,
    padding: chatTokens.space[3],
    gap: chatTokens.space[3],
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
  },
  settingsAvatarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[3],
  },
  settingsAvatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  settingsAvatarText: {
    flex: 1,
    gap: 6,
  },
  settingsLabel: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  settingsField: {
    gap: chatTokens.space[2],
  },
  settingsInput: {
    height: 44,
    borderRadius: chatTokens.radius.md,
    borderWidth: 1,
    borderColor: chatTokens.colors.border,
    paddingHorizontal: 12,
    fontSize: chatTokens.type.base,
    color: chatTokens.colors.text,
    backgroundColor: 'rgba(255,255,255,0.9)',
  },
  settingsHint: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  sheetOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.2)',
    justifyContent: 'flex-end',
  },
  sheetBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  sheetContainer: {
    padding: chatTokens.space[3],
  },
  sheetCard: {
    borderRadius: chatTokens.radius.lg,
    padding: chatTokens.space[3],
    gap: chatTokens.space[2],
    ...glassSurface,
  },
  sheetTitle: {
    fontSize: chatTokens.type.title,
    fontWeight: chatTokens.type.weightBold,
    color: chatTokens.colors.text,
  },
  sheetItem: {
    height: 44,
    borderRadius: chatTokens.radius.md,
    paddingHorizontal: 12,
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.7)',
  },
  sheetItemPressed: {
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  sheetItemText: {
    fontSize: chatTokens.type.base,
    color: chatTokens.colors.text,
  },
  sheetItemDanger: {
    color: '#C13645',
  },
  forwardContainer: {
    padding: chatTokens.space[3],
  },
  forwardList: {
    maxHeight: 360,
    marginTop: chatTokens.space[2],
  },
  forwardListContent: {
    gap: chatTokens.space[2],
    paddingBottom: chatTokens.space[2],
  },
  forwardItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: chatTokens.space[2],
    padding: chatTokens.space[2],
    borderRadius: chatTokens.radius.lg,
    backgroundColor: 'rgba(255,255,255,0.7)',
  },
  forwardItemPressed: {
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  forwardItemText: {
    flex: 1,
    minWidth: 0,
  },
  forwardItemTitle: {
    fontSize: chatTokens.type.base,
    fontWeight: chatTokens.type.weightBold,
    color: chatTokens.colors.text,
  },
  forwardItemSubtitle: {
    fontSize: chatTokens.type.small,
    color: chatTokens.colors.textSubtle,
  },
  emojiPickerContainer: {
    padding: chatTokens.space[3],
  },
  emojiPickerCard: {
    borderRadius: chatTokens.radius.lg,
    overflow: 'hidden',
    backgroundColor: 'rgba(255,255,255,0.9)',
  },
})
